<template>
  <section class="playground-workspace">
    <header class="playground-head">
      <div class="playground-head-main">
        <div class="playground-brand">
          <span><img :src="yuntuLogo" alt="耘图AI同创 Logo" /></span>
          <div>
            <strong>耘图对话</strong>
            <button
              class="system-prompt-preview"
              type="button"
              :disabled="!currentSystemPrompt"
              title="查看完整系统提示词"
              @click="showPromptDialog = true"
            >
              <span>{{ currentSystemPrompt || '正在加载系统提示词…' }}</span>
              <em v-if="currentSystemPrompt">查看全部</em>
            </button>
          </div>
        </div>
      </div>
      <div class="head-actions">
        <button type="button" :disabled="busy || optimizingPrompt" @click="historyOpen = true; loadConversations()">
          <i class="el-icon-time"></i> 历史对话
        </button>
        <button type="button" :disabled="busy || optimizingPrompt" @click="newConversation">
          <i class="el-icon-plus"></i> 新对话
        </button>
      </div>
    </header>

    <transition name="history-fade">
      <div v-if="historyOpen" class="history-layer" @click.self="historyOpen = false">
        <aside class="conversation-history" aria-label="历史对话">
          <header>
            <div><strong>历史对话</strong><small>仅显示当前账号的记录</small></div>
            <button type="button" aria-label="关闭历史对话" @click="historyOpen = false">×</button>
          </header>
          <button class="history-new" type="button" :disabled="busy" @click="newConversation">
            <i class="el-icon-plus"></i> 开始新对话
          </button>
          <div v-if="conversationsLoading" class="history-state"><i class="el-icon-loading"></i> 正在加载…</div>
          <div v-else-if="!conversations.length" class="history-state">还没有保存的对话</div>
          <div v-else class="history-list">
            <article
              v-for="item in conversations"
              :key="item.id"
              :class="{ active: item.id === conversationId }"
              @click="openConversation(item.id)"
            >
              <div>
                <strong>{{ item.title || '新对话' }}</strong>
                <p>{{ item.preview || modelLabel(item.model) }}</p>
                <small>{{ formatConversationTime(item.lastMessageAt || item.createdAt) }}</small>
              </div>
              <button type="button" title="删除这条对话" :disabled="busy" @click.stop="deleteConversation(item)">
                <i class="el-icon-delete"></i>
              </button>
            </article>
          </div>
          <AppPagination :page="conversationPage" :page-size="12" :total="conversationTotal" :loading="conversationsLoading" @change="changeConversationPage" />
        </aside>
      </div>
    </transition>

    <main ref="messageStage" class="message-stage">
      <div v-if="conversationLoading" class="conversation-loading"><i class="el-icon-loading"></i> 正在恢复对话…</div>
      <div v-if="!messages.length" class="welcome-state">
        <span><img :src="yuntuLogo" alt="耘图AI同创 Logo" /></span>
        <h2>你好！今天想做点什么？</h2>
        <p>通过文字和参考图开始图片创作，也可以切换到设计咨询</p>
        <div class="suggestion-grid">
          <button v-for="item in suggestions" :key="item.title" type="button" @click="useSuggestion(item)">
            <i :class="item.icon"></i><span><strong>{{ item.title }}</strong><small>{{ item.description }}</small></span>
          </button>
        </div>
      </div>

      <div v-else class="message-list">
        <article v-for="(message, index) in messages" :key="index" :class="['message', message.role]">
          <span class="message-avatar">
            <img v-if="message.role === 'assistant'" :src="yuntuLogo" alt="耘图AI同创 Logo" />
            <i v-else class="el-icon-user-solid"></i>
          </span>
          <div class="message-body">
            <div v-if="message.attachments && message.attachments.length" class="message-images">
              <button v-for="image in message.attachments" :key="image" type="button" @click="previewImage(image)">
                <img :src="$ossImageUrl(image, 'thumbnail')" alt="对话附件" />
              </button>
            </div>
            <div
              v-if="message.content && !(message.imageGeneration && message.loading)"
              :class="['message-content', { streaming: message.loading, markdown: message.role === 'assistant' }]"
              v-html="renderMessage(message)"
            ></div>
            <div v-if="message.imageGeneration && message.loading" class="image-generating-status" role="status" aria-live="polite">
              <span class="generation-visual" aria-hidden="true">
                <i class="generation-orbit"></i>
                <i class="el-icon-picture-outline"></i>
              </span>
              <span class="generation-copy">
                <strong>正在为你生成图片</strong>
                <small>平均需要 1–3 分钟，最长不超过 5 分钟</small>
                <em><i></i>生成期间请保持页面开启，完成后会自动显示</em>
              </span>
            </div>
            <div v-if="message.resultImage" class="generated-image">
              <div class="generated-image-media">
                <img :src="$ossImageUrl(message.resultImage, 'preview')" alt="Wintoo GPT 生成结果" @click="previewImage(message.resultImage)" />
                <div class="generated-image-actions">
                  <button
                    class="generated-image-edit-button"
                    type="button"
                    :disabled="busy || optimizingPrompt"
                    @click="openMaskEditor(message)"
                  >
                    <i class="el-icon-edit-outline" aria-hidden="true"></i>
                    局部编辑
                  </button>
                  <WorkflowTuningButton
                    class="generated-image-workflow-button"
                    variant="button"
                    :image-url="message.resultImage"
                    :prompt="workflowPromptFor(index)"
                    :source-model="message.model || ''"
                    :context-id="conversationId || ''"
                    module-name="耘图对话"
                    label="工作流"
                  />
                  <button type="button" @click="downloadImage(message.resultImage)"><i class="el-icon-download"></i> 下载图片</button>
                </div>
              </div>
              <ImageDimensionsRow :items="[{ label: '图片尺寸', url: message.resultImage, width: message.resultWidth, height: message.resultHeight }]" />
            </div>
            <span v-if="message.loading && !message.content" class="typing"><i></i><i></i><i></i></span>
            <div
              v-if="message.model || message.createdAt"
              class="message-meta"
              :aria-label="message.model ? '本条回复的模型与生成时间' : '消息时间'"
            >
              <span v-if="message.model" class="message-meta-item message-meta-model">
                <i class="el-icon-cpu" aria-hidden="true"></i>
                <span class="message-meta-label">{{ message.imageGeneration ? '生成模型' : '回复模型' }}</span>
                <strong>{{ modelLabel(message.model) }}</strong>
              </span>
              <span v-if="message.createdAt" class="message-meta-item message-meta-time">
                <i class="el-icon-time" aria-hidden="true"></i>
                <span class="message-meta-label">{{ message.imageGeneration ? '生成时间' : '消息时间' }}</span>
                <time :datetime="message.createdAt">{{ formatMessageTime(message.createdAt) }}</time>
              </span>
            </div>
          </div>
        </article>
      </div>
    </main>

    <footer
      :class="['composer-shell', { locked: imageGenerating, 'drag-active': dragActive }]"
      :aria-busy="String(imageGenerating || optimizingPrompt)"
      @dragenter.prevent="handleDragEnter"
      @dragover.prevent="handleDragOver"
      @dragleave.prevent="handleDragLeave"
      @drop.prevent="handleDrop"
    >
      <div v-if="uploads.length" class="attachment-strip">
        <div v-for="(item, index) in uploads" :key="item.localUrl">
          <img :src="item.localUrl" alt="待发送图片" />
          <span>{{ item.uploading ? '上传中…' : '已就绪' }}</span>
          <button type="button" aria-label="移除图片" :disabled="busy || optimizingPrompt" @click="removeUpload(index)">×</button>
        </div>
      </div>

      <div class="quick-prompt-row">
        <span><i class="el-icon-magic-stick"></i> 快捷指令</span>
        <div role="list" :aria-label="mode === 'image' ? '常用画图指令' : '常用对话指令'">
          <button
            v-for="item in quickPrompts"
            :key="item"
            type="button"
            role="listitem"
            :disabled="busy || optimizingPrompt"
            :title="item"
            :aria-label="`使用快捷指令：${item}`"
            @click="applyQuickPrompt(item)"
          >
            {{ item }}
          </button>
        </div>
      </div>

      <textarea ref="promptInput" v-model="prompt" :disabled="busy" :readonly="optimizingPrompt" rows="1"
        :aria-busy="String(optimizingPrompt)"
        :placeholder="mode === 'chat' ? '给 Wintoo GPT 发送消息…' : '描述你想生成或修改的图片…'"
        @paste="handlePaste"
        @keydown.enter.exact.prevent="submit"></textarea>

      <div :class="['composer-toolbar', `mode-${mode}`]">
        <div class="toolbar-left">
          <label class="attach-button" title="添加图片">
            <input type="file" accept="image/png,image/jpeg,image/webp" multiple :disabled="busy || optimizingPrompt" @change="handleFiles" />
            <i class="el-icon-plus"></i>
            <span class="visually-hidden">添加图片</span>
          </label>
          <div class="compact-mode-switch" role="tablist" aria-label="选择创作模式">
            <button
              type="button"
              role="tab"
              :aria-selected="String(mode === 'image')"
              :class="{ active: mode === 'image' }"
              :disabled="busy || optimizingPrompt"
              title="作图模式"
              aria-label="切换到作图模式"
              @click="setMode('image')"
            >
              <i class="el-icon-picture-outline"></i>
              <span>作图</span>
            </button>
            <button
              type="button"
              role="tab"
              :aria-selected="String(mode === 'chat')"
              :class="{ active: mode === 'chat' }"
              :disabled="busy || optimizingPrompt"
              title="对话模式"
              aria-label="切换到对话模式"
              @click="setMode('chat')"
            >
              <i class="el-icon-chat-dot-round"></i>
              <span>对话</span>
            </button>
          </div>
          <span class="model-badge" :aria-label="`当前使用模型：${mode === 'image' ? 'FastIMG' : modelLabel(chatModel)}`">
            {{ mode === 'image' ? 'FastIMG' : modelLabel(chatModel) }}
          </span>
          <template v-if="mode === 'image'">
            <el-select v-model="ratio" size="mini" :disabled="busy || optimizingPrompt" class="small-select">
              <el-option v-for="item in ratios" :key="item" :label="item" :value="item" />
            </el-select>
            <el-select v-model="resolution" size="mini" :disabled="busy || optimizingPrompt" class="small-select">
              <el-option label="2K" value="2K" /><el-option label="4K" value="4K" />
            </el-select>
          </template>
        </div>

        <div class="composer-secondary-controls">
          <QualitySelector v-if="mode === 'image'" class="composer-quality-selector" v-model="imageQuality" compact :disabled="busy || optimizingPrompt" :show-label="false" :show-value="false" />
          <button
            v-if="mode === 'image'"
            class="prompt-optimize-button"
            type="button"
            :disabled="!canOptimizePrompt"
            :aria-label="optimizingPrompt ? 'Wintoo GPT-5.6 正在优化提示词' : '使用 Wintoo GPT-5.6 优化提示词'"
            :title="optimizingPrompt ? '正在优化，请稍候' : '使用 Wintoo GPT-5.6 优化当前提示词并回填到输入框'"
            @click="optimizePrompt"
          >
            <i :class="optimizingPrompt ? 'el-icon-loading' : 'el-icon-magic-stick'" aria-hidden="true"></i>
            <span class="prompt-optimize-label-full">{{ optimizingPrompt ? '优化中…' : 'AI优化提示词' }}</span>
            <span class="prompt-optimize-label-mobile">{{ optimizingPrompt ? '优化中…' : 'AI优化提示词' }}</span>
          </button>
          <button class="send-button" type="button" :disabled="!canSubmit" aria-label="发送" @click="submit">
            <i :class="busy ? 'el-icon-loading' : 'el-icon-top'"></i>
          </button>
        </div>
      </div>
      <div v-if="promptBeforeOptimization" class="optimization-feedback" role="status" aria-live="polite">
        <span><i class="el-icon-circle-check" aria-hidden="true"></i> 已由 Wintoo GPT-5.6 优化，可继续修改或直接发送</span>
        <button type="button" :disabled="busy || optimizingPrompt" @click="undoPromptOptimization">撤销优化</button>
      </div>
      <div v-if="errorMessage" class="composer-error"><i class="el-icon-warning-outline"></i>{{ errorMessage }}</div>
      <div v-if="dragActive && !busy && !imageGenerating" class="composer-drop-overlay" aria-hidden="true">
        <i class="el-icon-upload2"></i>
        <span>松开以上传图片</span>
      </div>
      <div v-if="imageGenerating" class="composer-lock" role="status" aria-live="polite">
        <span class="generation-visual compact" aria-hidden="true">
          <i class="generation-orbit"></i>
          <i class="el-icon-picture-outline"></i>
        </span>
        <span class="generation-copy">
          <strong>图片正在创作中，请耐心等待</strong>
          <small>平均需要 1–3 分钟，最长不超过 5 分钟</small>
          <em><i></i>请保持页面开启，完成后即可继续操作</em>
        </span>
      </div>
    </footer>

    <ReusableImageEditor
      :visible="maskEditor.visible"
      :source-url="maskEditor.sourceUrl"
      :submitting="maskEditor.submitting"
      :error="maskEditor.error"
      :conversation-id="conversationId"
      title="图片编辑"
      prompt-label="编辑提示词"
      prompt-placeholder="说明想把标记区域改成什么"
      optimizer-context="耘图对话局部编辑：结合当前图片和可视标记区域优化修改提示词，未标记区域保持原样。"
      @close="closeMaskEditor()"
      @save="submitMaskedEdit"
    />

    <el-dialog
      title="当前系统提示词"
      :visible.sync="showPromptDialog"
      width="min(720px, calc(100vw - 24px))"
      custom-class="system-prompt-dialog"
      append-to-body
    >
      <pre>{{ currentSystemPrompt }}</pre>
    </el-dialog>
  </section>
</template>

<script>
/*
 * 耘图 GPT 对话：文字对话使用 SSE，图片创作使用异步 generations。
 * 事件顺序必须保持 ready -> chunk* -> done/error；图片 202 只代表已入队，仍需轮询 records。
 */
import { apiRequest, downloadResource } from '@/services/api'
import { isConversationRequest, isImageGenerationRequest } from '@/utils/playgroundIntent'
import { closestImageRatio, DEFAULT_IMAGE_RATIO, readImageDimensions, SUPPORTED_IMAGE_RATIOS } from '@/utils/imageAspectRatio'
import MarkdownIt from 'markdown-it'
import yuntuLogo from '@/assets/yuntu-logo.jpg'
import ImageDimensionsRow from '@/components/ImageDimensionsRow.vue'
import QualitySelector from '@/components/QualitySelector.vue'
import ReusableImageEditor from '@/components/ReusableImageEditor.vue'
import { DEFAULT_GENERATION_QUALITY, IMAGE_GENERATION_MODEL } from '@/utils/outputQuality'
import { formatModelName, formatUserFacingBrand } from '@/utils/modelDisplay'
import { downloadOriginalImageBlob } from '@/utils/imageDownload'

const DEFAULT_IMAGE_MODEL = IMAGE_GENERATION_MODEL
const DEFAULT_IMAGE_RESOLUTION = '2K'
const DEFAULT_CHAT_MODEL = 'gpt-5.6-terra'
let playgroundDownloadSequence = 0

function createMaskEditorState(overrides = {}) {
  return {
    visible: false,
    submitting: false,
    sourceUrl: '',
    width: 0,
    height: 0,
    error: '',
    ...overrides
  }
}

function padNumber(value, length = 2) {
  return String(value).padStart(length, '0')
}

function uniquePlaygroundImageName() {
  const now = new Date()
  playgroundDownloadSequence = (playgroundDownloadSequence + 1) % 1000
  let randomPart = Math.random().toString(36).slice(2, 8)
  if (typeof window !== 'undefined' && window.crypto && typeof window.crypto.getRandomValues === 'function') {
    const randomValue = new Uint32Array(1)
    window.crypto.getRandomValues(randomValue)
    randomPart = randomValue[0].toString(36).padStart(6, '0').slice(-6)
  }
  const datePart = `${now.getFullYear()}${padNumber(now.getMonth() + 1)}${padNumber(now.getDate())}`
  const timePart = `${padNumber(now.getHours())}${padNumber(now.getMinutes())}${padNumber(now.getSeconds())}-${padNumber(now.getMilliseconds(), 3)}`
  const sequencePart = padNumber(playgroundDownloadSequence, 3)
  return `耘图对话-${datePart}-${timePart}-${sequencePart}-${randomPart}`
}

const markdown = new MarkdownIt({
  html: false,
  breaks: true,
  linkify: true,
  typographer: false
})


function escapeHtml(value) {
  return String(value || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;')
    .replace(/\n/g, '<br>')
}

export default {
  name: 'GptPlayground',
  components: { ImageDimensionsRow, QualitySelector, ReusableImageEditor },
  data() {
    return {
      yuntuLogo,
      mode: 'image', prompt: '', model: DEFAULT_IMAGE_MODEL, chatModel: DEFAULT_CHAT_MODEL, temperature: 0.7, maxTokens: 4096,
      optimizingPrompt: false, promptBeforeOptimization: '',
      ratio: DEFAULT_IMAGE_RATIO, resolution: DEFAULT_IMAGE_RESOLUTION, imageQuality: DEFAULT_GENERATION_QUALITY, busy: false, imageGenerating: false, dragActive: false, dragDepth: 0, errorMessage: '', messages: [], uploads: [],
      maskEditor: createMaskEditorState(),
      pollingStopped: false, requestControllers: [], modelPresets: [], showPromptDialog: false,
      conversationId: '', conversations: [], historyOpen: false, conversationsLoading: false, conversationLoading: false, conversationPage: 1, conversationTotal: 0,
      ratios: SUPPORTED_IMAGE_RATIOS,
      suggestions: [
        { mode: 'image', icon: 'el-icon-magic-stick', title: '图片创作', description: '根据描述或参考图生成新方案', prompt: '请根据我的描述或上传的参考图，生成一张高质量室内设计效果图。' },
        { mode: 'chat', icon: 'el-icon-chat-line-round', title: '设计咨询', description: '帮我梳理一个空间的设计思路', prompt: '请作为专业室内设计顾问，帮我梳理这个空间的设计思路。' }
      ]
    }
  },
  computed: {
    currentPreset() {
      return this.modelPresets.find(item => item.model === this.model) || null
    },
    currentSystemPrompt() {
      return this.currentPreset && this.currentPreset.systemPrompt
        ? this.currentPreset.systemPrompt
        : ''
    },
    quickPrompts() {
      if (this.mode === 'image') {
        return [
          '帮我生成一张新中式客厅装修效果图',
          '帮我生成一张奶油风卧室装修效果图',
          '帮我生成一张轻奢风餐厅装修效果图',
          '帮我生成一张原木风书房装修效果图',
          '帮我生成一张法式风格厨房装修效果图',
          '帮我生成一张侘寂风玄关装修效果图'
        ]
      }
      return [
        '客户觉得设计费贵了怎么回复？',
        '室内设计方案汇报应该怎么讲？',
        '室内设计说明怎么写？',
        '室内设计初次沟通要问客户什么？',
        '怎么向客户解释设计方案的价值？',
        '室内设计师常用的客户沟通话术有哪些？'
      ]
    },
    canSubmit() {
      return !this.busy && !this.optimizingPrompt && !this.uploads.some(item => item.uploading) && Boolean(this.prompt.trim() || (this.mode === 'chat' && this.uploads.length))
    },
    canOptimizePrompt() {
      return this.mode === 'image' && !this.busy && !this.optimizingPrompt && !this.uploads.some(item => item.uploading) && Boolean(this.prompt.trim())
    }
  },
  created() {
    this.loadPlaygroundPresets()
    this.loadConversations()
  },
  watch: {
    mode(nextMode) {
      const expectedModel = nextMode === 'image'
        ? DEFAULT_IMAGE_MODEL
        : this.chatModel
      if (this.model !== expectedModel) this.model = expectedModel
      this.promptBeforeOptimization = ''
      this.showPromptDialog = false
    }
  },
  beforeDestroy() {
    this.pollingStopped = true
    this.requestControllers.forEach(item => item.abort())
    this.uploads.forEach(item => URL.revokeObjectURL(item.localUrl))
  },
  methods: {
    request(url, options = {}) {
      return apiRequest(url, options, { controllers: this.requestControllers, isStopped: () => this.pollingStopped })
    },
    renderMessage(message) {
      return message.role === 'assistant' ? markdown.render(message.content || '') : escapeHtml(message.content)
    },
    modelLabel(value) {
      return formatModelName(value)
    },
    async loadPlaygroundPresets() {
      try {
        const result = await this.request('/api/prompts/playground-presets')
        this.modelPresets = Array.isArray(result.presets) ? result.presets : []
        const chatPreset = this.modelPresets.find(item => String(item.model || '').startsWith('gpt-') && !String(item.model || '').startsWith('gpt-image'))
        if (chatPreset) {
          this.chatModel = chatPreset.model
          if (this.mode === 'chat') this.model = chatPreset.model
        }
      } catch (error) {
        // Prompt metadata is explanatory; a temporary loading failure must not block chat.
      }
    },
    async loadConversations(openLatest = false) {
      if (this.conversationsLoading) return
      this.conversationsLoading = true
      try {
        const result = await this.request(`/api/playground/conversations/paged?page=${this.conversationPage}&pageSize=12`)
        this.conversations = result.items || []
        this.conversationTotal = Number(result.total || 0)
        if (openLatest && !this.conversationId && this.conversations.length) {
          await this.openConversation(this.conversations[0].id)
        }
      } catch (error) {
        if (openLatest) this.errorMessage = error.message || '历史对话加载失败'
      } finally {
        this.conversationsLoading = false
      }
    },
    changeConversationPage(page) {
      this.conversationPage = page
      this.loadConversations(false)
    },
    async ensureConversation() {
      if (this.conversationId) return this.conversationId
      const conversation = await this.request('/api/playground/conversations', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ model: this.model })
      })
      this.conversationId = conversation.id
      this.conversations = [conversation, ...this.conversations.filter(item => item.id !== conversation.id)]
      return conversation.id
    },
    async openConversation(id) {
      if (!id || this.busy || this.optimizingPrompt || (id === this.conversationId && this.messages.length)) {
        this.historyOpen = false
        return
      }
      this.conversationLoading = true
      this.errorMessage = ''
      try {
        const conversation = await this.request(`/api/playground/conversations/${encodeURIComponent(id)}`)
        this.conversationId = conversation.id
        const conversationModel = String(conversation.model || '')
        const hasImageMessages = (conversation.messages || []).some(item => item.kind === 'image_result')
        if (conversationModel.startsWith('gpt-image-2-') || hasImageMessages) {
          this.mode = 'image'
          this.model = DEFAULT_IMAGE_MODEL
          this.imageQuality = DEFAULT_GENERATION_QUALITY
        } else {
          this.mode = 'chat'
          this.model = this.chatModel
        }
        this.messages = (conversation.messages || []).map(item => ({
          id: item.id,
          role: item.role,
          content: item.content || '',
          attachments: (item.attachments || []).map(asset => asset.recordUrl || asset.remoteUrl).filter(Boolean),
          loading: item.status === 'streaming' || item.status === 'generating',
          imageGeneration: item.kind === 'image_result',
          resultImage: item.resultImage || '',
          resultWidth: item.resultWidth || null,
          resultHeight: item.resultHeight || null,
          generationBatchId: item.generationBatchId || '',
          errorMessage: item.errorMessage || '',
          model: item.model,
          createdAt: item.createdAt || item.updatedAt || ''
        }))
        this.promptBeforeOptimization = ''
        this.historyOpen = false
        this.scrollToBottom()
        const generating = this.messages.find(item => item.imageGeneration && item.loading && item.generationBatchId)
        if (generating) this.resumeGeneratingImage(conversation.id, generating)
      } catch (error) {
        this.errorMessage = error.message || '对话恢复失败'
        if (this.conversationId === id) this.conversationId = ''
      } finally {
        this.conversationLoading = false
      }
    },
    async resumeGeneratingImage(conversationId, message) {
      if (this.imageGenerating) return
      this.busy = true
      this.imageGenerating = true
      message.loading = true
      message.content = message.content || '任务已提交，正在后台生成图片…'
      try {
        const taskId = await this.waitForTaskId(message.generationBatchId)
        await this.pollImage(taskId)
        if (this.conversationId === conversationId) await this.reloadCurrentConversation()
      } catch (error) {
        if (this.conversationId === conversationId) {
          if (error.pending) {
            message.content = error.message
            window.setTimeout(() => this.resumeGeneratingImage(conversationId, message), 3000)
          } else {
            message.loading = false
            message.content = `图片生成未完成：${error.message || '请稍后重试。'}`
            this.errorMessage = error.message || 'Wintoo GPT 图片生成失败'
          }
        }
      } finally {
        this.busy = false
        this.imageGenerating = false
      }
    },
    async reloadCurrentConversation() {
      const id = this.conversationId
      this.conversationId = ''
      await this.openConversation(id)
    },
    newConversation() {
      if (this.busy || this.optimizingPrompt) return
      this.uploads.forEach(item => URL.revokeObjectURL(item.localUrl))
      this.uploads = []
      this.messages = []
      this.prompt = ''
      this.promptBeforeOptimization = ''
      this.errorMessage = ''
      this.conversationId = ''
      this.historyOpen = false
      this.mode = 'image'
      this.model = DEFAULT_IMAGE_MODEL
      this.ratio = DEFAULT_IMAGE_RATIO
      this.resolution = DEFAULT_IMAGE_RESOLUTION
      this.imageQuality = DEFAULT_GENERATION_QUALITY
    },
    async deleteConversation(item) {
      if (this.busy || this.optimizingPrompt) return
      try {
        await this.$confirm(`确定删除“${item.title || '这条对话'}”吗？`, '删除对话', {
          confirmButtonText: '删除',
          cancelButtonText: '取消',
          type: 'warning',
          modal: false,
          lockScroll: false,
          closeOnClickModal: false
        })
        await this.request(`/api/playground/conversations/${encodeURIComponent(item.id)}`, { method: 'DELETE' })
        this.conversations = this.conversations.filter(conversation => conversation.id !== item.id)
        if (this.conversationId === item.id) {
          this.conversationId = ''
          this.messages = []
          if (this.conversations.length) await this.openConversation(this.conversations[0].id)
        }
      } catch (error) {
        if (error !== 'cancel' && error !== 'close') this.$message.error(error.message || '删除失败')
      }
    },
    formatConversationTime(value) {
      if (!value) return ''
      const date = new Date(value)
      if (Number.isNaN(date.getTime())) return ''
      return this.formatMessageTime(date)
    },
    formatMessageTime(value) {
      if (!value) return ''
      const date = value instanceof Date ? value : new Date(value)
      if (Number.isNaN(date.getTime())) return ''
      const pad = number => String(number).padStart(2, '0')
      return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}`
    },
    setMode(mode) {
      this.mode = mode
    },
    useSuggestion(item) {
      if (this.busy || this.optimizingPrompt) return
      this.setMode(item.mode)
      this.$nextTick(() => {
        this.prompt = this.quickPrompts[0] || item.prompt
        this.$refs.promptInput.focus()
      })
    },
    applyQuickPrompt(value) {
      if (this.busy || this.optimizingPrompt) return
      this.prompt = value
      this.promptBeforeOptimization = ''
      this.errorMessage = ''
      this.$nextTick(() => {
        if (this.$refs.promptInput) this.$refs.promptInput.focus()
      })
    },
    handleFiles(event) {
      const files = Array.from(event.target.files || [])
      event.target.value = ''
      this.uploadFiles(files)
    },
    handlePaste(event) {
      if (this.busy || this.imageGenerating || this.optimizingPrompt) return
      const items = Array.from((event.clipboardData && event.clipboardData.items) || [])
      const imageFiles = items
        .filter(item => item.kind === 'file' && item.type.startsWith('image/'))
        .map(item => item.getAsFile())
        .filter(Boolean)
      if (!imageFiles.length) return
      event.preventDefault()
      this.uploadFiles(imageFiles)
    },
    hasDraggedFiles(event) {
      const types = Array.from((event.dataTransfer && event.dataTransfer.types) || [])
      return types.includes('Files')
    },
    handleDragEnter(event) {
      if (this.busy || this.imageGenerating || this.optimizingPrompt || !this.hasDraggedFiles(event)) return
      this.dragDepth += 1
      this.dragActive = true
    },
    handleDragOver(event) {
      if (this.busy || this.imageGenerating || this.optimizingPrompt || !this.hasDraggedFiles(event)) return
      if (event.dataTransfer) event.dataTransfer.dropEffect = 'copy'
      this.dragActive = true
    },
    handleDragLeave() {
      if (!this.dragActive) return
      this.dragDepth = Math.max(0, this.dragDepth - 1)
      if (this.dragDepth === 0) this.dragActive = false
    },
    handleDrop(event) {
      this.dragActive = false
      this.dragDepth = 0
      if (this.busy || this.imageGenerating || this.optimizingPrompt) return
      this.uploadFiles(Array.from((event.dataTransfer && event.dataTransfer.files) || []))
    },
    async uploadFiles(inputFiles) {
      const available = Math.max(0, 6 - this.uploads.length)
      if (!available) {
        this.$message.warning('最多可上传 6 张图片')
        return
      }
      const incomingFiles = Array.from(inputFiles || [])
      const files = incomingFiles.slice(0, available)
      if (incomingFiles.length > available) this.$message.warning('最多可上传 6 张图片，已保留前面的图片')
      for (const file of files) {
        if (!['image/png', 'image/jpeg', 'image/webp'].includes(file.type) || file.size >= 10 * 1024 * 1024) {
          this.$message.warning('仅支持 10MB 以内的 JPG、PNG、WebP 图片')
          continue
        }
        const shouldFollowOriginal = this.uploads.length === 0
        const item = { file, localUrl: URL.createObjectURL(file), remoteUrl: '', uploading: true }
        this.uploads.push(item)
        if (shouldFollowOriginal) {
          try {
            const dimensions = await readImageDimensions(file)
            this.ratio = closestImageRatio(dimensions.width, dimensions.height, this.ratios)
          } catch (error) {
            this.ratio = DEFAULT_IMAGE_RATIO
          }
        }
        try {
          const form = new FormData()
          form.append('file', file)
          const asset = await this.request('/api/images/upload', {
            method: 'POST',
            body: form,
            timeoutMs: 120000,
            timeoutMessage: '图片上传超时，请检查网络后重试',
            retries: 1,
            retryDelayMs: 1000
          })
          item.remoteUrl = asset.url
          item.recordUrl = asset.localUrl || ''
          this.$message.success('图片上传成功')
        } catch (error) {
          this.$message.error(error.message || '图片上传失败')
          this.removeUpload(this.uploads.indexOf(item))
        } finally {
          item.uploading = false
        }
      }
    },
    removeUpload(index) {
      const item = this.uploads[index]
      if (!item) return
      URL.revokeObjectURL(item.localUrl)
      this.uploads.splice(index, 1)
    },
    openMaskEditor(message) {
      const sourceUrl = message && message.resultImage ? String(message.resultImage) : ''
      if (!sourceUrl || this.busy || this.optimizingPrompt) return
      this.maskEditor = createMaskEditorState({
        visible: true,
        sourceUrl,
        width: Number(message.resultWidth || 0),
        height: Number(message.resultHeight || 0)
      })
    },
    closeMaskEditor(force = false) {
      if (this.maskEditor.submitting && !force) return
      this.maskEditor = createMaskEditorState()
    },
    async submitMaskedEdit(payload) {
      if (this.maskEditor.submitting || !payload || !payload.sourceUrl || !payload.blob || !payload.prompt) return
      let sourceUrl = payload.sourceUrl
      const text = payload.prompt.trim()
      const visibleText = String(payload.rawPrompt || payload.prompt).trim()
      const outputRatio = closestImageRatio(payload.width, payload.height, this.ratios)
      this.maskEditor.submitting = true
      this.maskEditor.error = ''
      let assistant = null
      let conversationId = ''
      try {
        conversationId = await this.ensureConversation()
        const markedForm = new FormData()
        markedForm.append('file', payload.blob, `playground-marked-${Date.now()}.png`)
        const markedAsset = await this.request('/api/images/upload', {
          method: 'POST',
          body: markedForm,
          timeoutMs: 120000,
          timeoutMessage: '标记图片上传超时，请检查网络后重试'
        })
        if (!markedAsset.url) throw new Error('标记图片上传后未返回可用地址')
        sourceUrl = markedAsset.url

        const random = window.crypto && window.crypto.randomUUID
          ? window.crypto.randomUUID()
          : `${Date.now()}-${Math.random().toString(36).slice(2)}`
        const batch = await this.request(`/api/generations?conversationId=${encodeURIComponent(conversationId)}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            imageUrls: [sourceUrl],
            prompt: text,
            space: '耘图对话',
            style: '标注局部编辑',
            size: this.ratios.includes(outputRatio) ? outputRatio : DEFAULT_IMAGE_RATIO,
            resolution: this.resolution,
            quality: this.imageQuality,
            count: 1,
            stage: 'playground',
            floorPlanRecordUrls: [],
            referenceRecordUrls: [],
            requestId: `playground-mark-${random}`.slice(0, 64)
          }),
          timeoutMs: 120000
        })

        const createdAt = new Date().toISOString()
        this.messages.push({ role: 'user', content: visibleText, attachments: [sourceUrl], createdAt })
        assistant = {
          role: 'assistant',
          content: '正在根据标记区域进行局部修改…',
          loading: true,
          imageGeneration: true,
          model: this.model,
          resultImage: '',
          generationBatchId: batch.batchId,
          createdAt
        }
        this.messages.push(assistant)
        this.maskEditor.submitting = false
        this.closeMaskEditor(true)
        this.busy = true
        this.imageGenerating = true
        this.scrollToBottom()

        const immediateTask = (batch.tasks || []).find(item => item && item.id)
        const taskId = immediateTask ? immediateTask.id : await this.waitForTaskId(batch.batchId, assistant)
        assistant.resultImage = await this.pollImage(taskId)
        if (!assistant.resultImage) throw new Error('Wintoo GPT 未返回局部编辑结果')
        assistant.content = '局部修改已完成。你可以继续标记新区域，或直接描述下一步调整。'
      } catch (error) {
        if (!assistant) {
          this.maskEditor.error = error.message || '局部编辑提交失败，请稍后重试'
          this.$message.error(this.maskEditor.error)
        } else if (error.pending) {
          assistant.content = error.message
          window.setTimeout(() => this.resumeGeneratingImage(conversationId, assistant), 3000)
        } else {
          assistant.content = `局部编辑未完成：${error.message || '请稍后重试。'}`
          this.errorMessage = error.message || 'Wintoo GPT 局部编辑失败'
        }
      } finally {
        if (this.maskEditor.visible) this.maskEditor.submitting = false
        if (assistant) {
          assistant.loading = false
          this.imageGenerating = false
          this.busy = false
          this.scrollToBottom()
          this.loadConversations()
        }
      }
    },
    async submit() {
      if (!this.canSubmit) return
      this.promptBeforeOptimization = ''
      const imageRequest = this.isImageGenerationRequest(this.prompt)
      const conversationRequest = this.isConversationRequest(this.prompt)
      if (this.mode === 'image' && conversationRequest) {
        this.mode = 'chat'
        this.model = this.chatModel
        this.$message.info('已识别为对话需求，不会生成图片')
        await this.submitChat()
      } else if (this.mode === 'chat' && imageRequest) {
        this.mode = 'image'
        this.model = DEFAULT_IMAGE_MODEL
        this.$message.info('已识别为图片生成需求')
        await this.submitImage()
      } else if (this.mode === 'image') await this.submitImage()
      else await this.submitChat()
    },
    async optimizePrompt() {
      if (!this.canOptimizePrompt) return
      const originalPrompt = this.prompt.trim()
      this.optimizingPrompt = true
      this.errorMessage = ''
      try {
        const result = await this.request('/api/playground/optimize-prompt', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            prompt: originalPrompt,
            imageUrls: this.uploads.map(item => item.remoteUrl).filter(Boolean),
            conversationId: this.conversationId || null
          }),
          timeoutMs: 120000,
          timeoutMessage: 'AI 优化提示词超时，请稍后重试',
          retries: 1,
          retryDelayMs: 800
        })
        const optimized = String(result.prompt || '').trim()
        if (!optimized) throw new Error('Wintoo GPT-5.6 未返回可用的优化提示词')
        this.promptBeforeOptimization = originalPrompt
        this.prompt = optimized
        if (result.degraded) {
          this.$message.warning(result.message || '图片理解过程中出现问题，本次已先根据文字做了浅层优化，结果未结合图片细节。')
        } else {
          this.$message.success('提示词已优化，可继续修改或直接发送')
        }
        this.$nextTick(() => {
          if (this.$refs.promptInput) this.$refs.promptInput.focus()
        })
      } catch (error) {
        this.errorMessage = error.message || 'AI 优化提示词失败，请稍后重试'
        this.$message.error(this.errorMessage)
      } finally {
        this.optimizingPrompt = false
      }
    },
    undoPromptOptimization() {
      if (!this.promptBeforeOptimization || this.busy || this.optimizingPrompt) return
      this.prompt = this.promptBeforeOptimization
      this.promptBeforeOptimization = ''
      this.$nextTick(() => {
        if (this.$refs.promptInput) this.$refs.promptInput.focus()
      })
    },
    isImageGenerationRequest(value) {
      return isImageGenerationRequest(value, this.uploads.length > 0)
    },
    isConversationRequest(value) {
      return isConversationRequest(value, this.uploads.length > 0)
    },
    consumeUploads() {
      const items = this.uploads.slice()
      this.uploads = []
      return items
    },
    async requestChatStream(body, onChunk) {
      // 原生 fetch 用于读取 SSE；不要改成 requestJson，否则 chunk 会被等到完整响应后才返回。
      const controller = new AbortController()
      this.requestControllers.push(controller)
      const timeoutId = window.setTimeout(() => controller.abort(), 180000)
      try {
        const response = await window.fetch('/api/playground/chat-stream', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'text/event-stream' },
          body: JSON.stringify(body),
          signal: controller.signal
        })
        if (!response.ok) {
          const responseText = await response.text()
          let message = `请求失败（${response.status}）`
          try {
            message = formatUserFacingBrand(JSON.parse(responseText).message) || message
          } catch (error) {
            // Keep the HTTP status when the response is not JSON.
          }
          throw new Error(message)
        }
        if (!response.body) throw new Error('当前浏览器不支持流式输出')

        const reader = response.body.getReader()
        const decoder = new TextDecoder('utf-8')
        let buffer = ''
        let finalContent = ''
        let streamError = ''
        const handleEvent = frame => {
          let eventName = 'message'
          const dataLines = []
          frame.split('\n').forEach(line => {
            if (line.startsWith('event:')) eventName = line.slice(6).trim()
            if (line.startsWith('data:')) dataLines.push(line.slice(5).trimStart())
          })
          if (!dataLines.length) return
          let payload = {}
          try {
            payload = JSON.parse(dataLines.join('\n'))
          } catch (error) {
            return
          }
          if (eventName === 'chunk' && payload.text) onChunk(String(payload.text))
          if (eventName === 'done') finalContent = String(payload.content || '')
          if (eventName === 'error') streamError = formatUserFacingBrand(payload.message) || 'Wintoo GPT 对话失败，请稍后重试'
        }

        while (true) {
          const { value, done } = await reader.read()
          buffer += decoder.decode(value || new Uint8Array(), { stream: !done }).replace(/\r\n/g, '\n')
          let boundary = buffer.indexOf('\n\n')
          while (boundary >= 0) {
            handleEvent(buffer.slice(0, boundary))
            buffer = buffer.slice(boundary + 2)
            boundary = buffer.indexOf('\n\n')
          }
          if (streamError) {
            await reader.cancel()
            throw new Error(streamError)
          }
          if (done) break
        }
        if (buffer.trim()) handleEvent(buffer)
        if (streamError) throw new Error(streamError)
        return finalContent
      } catch (error) {
        if (error.name === 'AbortError') throw new Error('Wintoo GPT 响应超时，请稍后重试')
        throw error
      } finally {
        window.clearTimeout(timeoutId)
        const index = this.requestControllers.indexOf(controller)
        if (index >= 0) this.requestControllers.splice(index, 1)
      }
    },
    async submitChat() {
      // 先把本地消息渲染成 loading，再创建/复用会话并持续接收 chunk，保证用户立即看到反馈。
      let conversationId
      try {
        conversationId = await this.ensureConversation()
      } catch (error) {
        this.errorMessage = error.message || '无法创建对话，请稍后重试'
        return
      }
      const text = this.prompt.trim() || '请分析我上传的图片。'
      const attached = this.consumeUploads()
      this.prompt = ''
      this.errorMessage = ''
      const createdAt = new Date().toISOString()
      this.messages.push({ role: 'user', content: text, attachments: attached.map(item => item.localUrl), createdAt })
      const assistant = { role: 'assistant', content: '', loading: true, model: this.model, createdAt }
      this.messages.push(assistant)
      this.busy = true
      this.scrollToBottom()
      let pendingText = ''
      let revealFrame = 0
      let revealResolver = null
      const revealNext = () => {
        if (!pendingText) {
          revealFrame = 0
          if (revealResolver) {
            revealResolver()
            revealResolver = null
          }
          return
        }
        const step = pendingText.length > 240
          ? 8
          : pendingText.length > 120
            ? 4
            : pendingText.length > 60
              ? 2
              : 1
        assistant.content += pendingText.slice(0, step)
        pendingText = pendingText.slice(step)
        this.scrollToBottom()
        revealFrame = window.requestAnimationFrame(revealNext)
      }
      const queueChunk = chunk => {
        pendingText += chunk
        if (!revealFrame) revealFrame = window.requestAnimationFrame(revealNext)
      }
      const waitForReveal = () => {
        if (!pendingText && !revealFrame) return Promise.resolve()
        return new Promise(resolve => { revealResolver = resolve })
      }
      try {
        const finalContent = await this.requestChatStream({
          prompt: text, model: this.model, temperature: this.temperature, maxTokens: this.maxTokens,
          imageUrls: attached.map(item => item.remoteUrl).filter(Boolean),
          imageRecordUrls: attached.map(item => item.recordUrl).filter(Boolean),
          conversationId
        }, queueChunk)
        await waitForReveal()
        if (finalContent && assistant.content.trim() !== finalContent.trim()) assistant.content = finalContent
        if (!assistant.content) throw new Error('Wintoo GPT 未返回可用内容')
      } catch (error) {
        if (revealFrame) window.cancelAnimationFrame(revealFrame)
        revealFrame = 0
        const message = error.message || 'Wintoo GPT 对话请求失败'
        assistant.content = assistant.content
          ? `${assistant.content}\n\n请求中断：${message}`
          : `请求未完成：${message}`
        this.errorMessage = message
      } finally {
        assistant.loading = false
        this.busy = false
        this.scrollToBottom()
        this.loadConversations()
      }
    },
    taskImage(task) {
      const data = task && task.result && task.result.data
      return Array.isArray(data) && data[0] ? data[0].url : ''
    },
    async pollImage(id) {
      // 图片任务由后端轮询 ToAPIs；前端只查询自有 taskId，不直接访问供应商。
      const started = Date.now()
      while (Date.now() - started < 600000 && !this.pollingStopped) {
        const task = await this.request(`/api/generations/${encodeURIComponent(id)}`, { timeoutMs: 15000, retries: 1 })
        if (task.status === 'completed') return this.taskImage(task)
        if (task.status === 'failed') {
          const details = task.error || {}
          const reason = details.message || 'Wintoo GPT 图片生成失败'
          const code = details.code ? `[${details.code}] ` : ''
          throw Object.assign(new Error(`${code}${reason}（任务：${id}）`), details)
        }
        await new Promise(resolve => window.setTimeout(resolve, 3000))
      }
      throw new Error('图片仍在生成，请稍后重试')
    },
    async waitForTaskId(batchId, assistant) {
      // 202 响应可能暂时没有 taskId，先查本地 batch 记录；超时按 pending 返回，不能提示“提交失败”。
      const started = Date.now()
      while (Date.now() - started < 300000 && !this.pollingStopped) {
        const record = await this.request(`/api/records/${encodeURIComponent(batchId)}`, { timeoutMs: 12000, retries: 1 })
        const task = (record.tasks || []).find(item => item && item.id)
        if (task) return task.id
        const failedTask = (record.tasks || []).find(item => item && item.status === 'failed')
        const details = failedTask && failedTask.error ? failedTask.error : {}
        const recordError = details.message || record.error || record.errorMessage
        if (recordError && assistant) assistant.content = `${details.code ? `[${details.code}] ` : ''}${recordError}`
        if (record.status === 'failed') throw Object.assign(new Error(`${details.code ? `[${details.code}] ` : ''}${recordError || 'Wintoo GPT 没有接受本次图片任务'}`), details)
        await new Promise(resolve => window.setTimeout(resolve, 2000))
      }
      const pending = new Error('任务已成功提交，仍在后台生成中，请稍后查看结果，无需重复提交。')
      pending.pending = true
      throw pending
    },
    async submitImage() {
      // 只创建一次 batch，后续等待 taskId 和图片完成；busy/imageGenerating 双重保护防止重复点击。
      if (this.imageGenerating) return
      let conversationId
      try {
        conversationId = await this.ensureConversation()
      } catch (error) {
        this.errorMessage = error.message || '无法创建对话，请稍后重试'
        return
      }
      const text = this.prompt.trim()
      const attached = this.consumeUploads()
      this.prompt = ''
      this.errorMessage = ''
      const createdAt = new Date().toISOString()
      this.messages.push({ role: 'user', content: text, attachments: attached.map(item => item.localUrl), createdAt })
      const assistant = {
        role: 'assistant',
        content: '正在生成图片…',
        loading: true,
        imageGeneration: true,
        model: this.model,
        resultImage: '',
        createdAt
      }
      this.messages.push(assistant)
      this.busy = true
      this.imageGenerating = true
      this.scrollToBottom()
      try {
        const random = window.crypto && window.crypto.randomUUID ? window.crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`
        const batch = await this.request(`/api/generations?conversationId=${encodeURIComponent(conversationId)}`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            imageUrls: attached.map(item => item.remoteUrl).filter(Boolean), prompt: text,
            space: '耘图对话', style: '自由创作', size: this.ratios.includes(this.ratio) ? this.ratio : DEFAULT_IMAGE_RATIO, resolution: this.resolution, quality: this.imageQuality,
            count: 1, stage: 'playground',
            floorPlanRecordUrls: attached.map(item => item.recordUrl).filter(Boolean).slice(0, 3),
            referenceRecordUrls: [], requestId: `playground-${random}`.slice(0, 64)
          }),
          timeoutMs: 120000
        })
        const immediateTask = (batch.tasks || []).find(item => item && item.id)
        assistant.generationBatchId = batch.batchId
        const taskId = immediateTask ? immediateTask.id : await this.waitForTaskId(batch.batchId, assistant)
        assistant.resultImage = await this.pollImage(taskId)
        if (!assistant.resultImage) throw new Error('Wintoo GPT 未返回图片')
        assistant.content = '图片已生成。接下来可以直接告诉我哪里要改，我会延续当前图片和本会话要求。'
      } catch (error) {
        if (error.pending) {
          assistant.content = error.message
          window.setTimeout(() => this.resumeGeneratingImage(conversationId, assistant), 3000)
        } else {
          assistant.content = `图片生成未完成：${error.message || '请稍后重试。'}`
          this.errorMessage = error.message || 'Wintoo GPT 图片生成失败'
        }
      } finally {
        assistant.loading = false
        this.imageGenerating = false
        this.busy = false
        this.scrollToBottom()
        this.loadConversations()
      }
    },
    scrollToBottom() {
      this.$nextTick(() => {
        const stage = this.$refs.messageStage
        if (stage) stage.scrollTop = stage.scrollHeight
      })
    },
    clearConversation() {
      this.newConversation()
    },
    workflowPromptFor(messageIndex) {
      const previousUserMessage = this.messages
        .slice(0, messageIndex)
        .reverse()
        .find(item => item && item.role === 'user' && String(item.content || '').trim())
      return previousUserMessage
        ? String(previousUserMessage.content).trim()
        : '请在保留原图主体、构图和空间关系的基础上，按我的新要求继续优化。'
    },
    previewImage(image) { this.$emit('preview-image', image, { title: '耘图对话图片' }) },
    async downloadImage(url) {
      try {
        const blob = await downloadResource(url)
        downloadOriginalImageBlob(blob, uniquePlaygroundImageName())
      } catch (error) { this.$message.error(error.message || '下载失败') }
    }
  }
}
</script>

<style scoped>
.playground-workspace{width:calc(100% - 68px);height:calc(100vh - 118px);min-height:620px;display:grid;grid-template-rows:auto minmax(0,1fr) auto;gap:12px;margin:18px auto 8px;color:#243029}.playground-workspace>*{min-width:0;max-width:100%}.playground-head{display:flex;align-items:center;justify-content:space-between}.playground-head-main{min-width:0;display:flex;align-items:center;gap:18px}.playground-brand{display:flex;align-items:center;gap:11px}.playground-brand>span{width:38px;height:38px;display:grid;place-items:center;border-radius:8px;background:#edf4ff;color:#2563eb;font-size:18px}.playground-brand>div{display:flex;flex-direction:column;gap:2px}.playground-brand strong{font-size:15px}.playground-brand small{color:#8a948e!important;font-size:9px!important}.playground-head>button{height:34px;border:1px solid #dce3de;border-radius:6px;padding:0 11px;background:#fff;color:#6c7770;font-size:9px}.message-stage{min-height:0;overflow-y:auto;scroll-behavior:smooth}.welcome-state{min-height:100%;display:flex;align-items:center;justify-content:center;flex-direction:column;padding:30px;text-align:center}.welcome-state>span{width:62px;height:62px;display:grid;place-items:center;border-radius:20px;background:#edf4ff;color:#2563eb;font-size:27px}.welcome-state h2{margin:16px 0 5px!important;font-size:23px!important}.welcome-state p{margin:0;font-size:11px!important;line-height:1.6}.suggestion-grid{width:min(720px,100%);display:grid;grid-template-columns:repeat(2,1fr);gap:9px;margin-top:24px}.suggestion-grid button{min-width:0;display:flex;align-items:center;gap:10px;border:1px solid #e1e6e2;border-radius:8px;padding:13px;background:#fff;color:#57635c;text-align:left}.suggestion-grid button:hover{border-color:#9eb7e3;background:#f8faff}.suggestion-grid button>i{width:30px;height:30px;display:grid;flex:0 0 30px;place-items:center;border-radius:6px;background:#edf4ff;color:#2563eb}.suggestion-grid span{min-width:0;display:flex;flex-direction:column;gap:3px}.suggestion-grid strong{font-size:10px}.suggestion-grid small{overflow:hidden;color:#8d9691!important;font-size:8px!important;text-overflow:ellipsis;white-space:nowrap}.message-list{width:min(900px,100%);margin:0 auto;padding:18px 0}.message{display:flex;align-items:flex-start;gap:10px;margin-bottom:22px}.message.user{flex-direction:row-reverse}.message-avatar{width:32px;height:32px;display:grid;flex:0 0 32px;place-items:center;border-radius:8px;background:#edf4ff;color:#2563eb}.message.user .message-avatar{background:#eff2ef;color:#657068}.message-body{max-width:min(720px,78%);display:flex;align-items:flex-start;flex-direction:column;gap:7px}.message.user .message-body{align-items:flex-end}.message-body>p{margin:0!important;border:1px solid #e2e7e3;border-radius:9px;padding:11px 13px;background:#fff;color:#344139!important;font-size:12px!important;line-height:1.7!important;white-space:pre-wrap}.message.user .message-body>p{border-color:#dce6f7;background:#edf4ff}.message-body>small{font-size:8px!important}.message-images{display:flex;flex-wrap:wrap;gap:6px}.message-images button{width:82px;height:64px;overflow:hidden;border:0;border-radius:6px;padding:0;background:#edf0ed}.message-images img{width:100%;height:100%;object-fit:cover}.generated-image{max-width:560px;overflow:hidden;position:relative;border:1px solid #dce3de;border-radius:8px;background:#fff}.generated-image img{width:100%;max-height:480px;display:block;object-fit:contain;cursor:zoom-in}.generated-image-actions{position:absolute;right:9px;bottom:9px;display:flex;gap:6px}.generated-image-actions button{border:0;border-radius:5px;padding:7px 10px;background:rgba(22,31,26,.82);color:#fff;font-size:9px}.typing{display:flex;gap:4px;border:1px solid #e2e7e3;border-radius:9px;padding:14px;background:#fff}.typing i{width:6px;height:6px;border-radius:50%;background:#7f9bd4;animation:typing 1.1s ease-in-out infinite}.typing i:nth-child(2){animation-delay:.15s}.typing i:nth-child(3){animation-delay:.3s}.composer-shell{border:1px solid #dce3de;border-radius:14px;padding:13px;background:#fff;box-shadow:0 14px 38px rgba(30,43,35,.1)}.composer-shell textarea{width:100%;min-height:48px;max-height:130px;resize:none;border:0!important;padding:4px 6px!important;outline:0;box-shadow:none!important;color:#354139;font-size:13px;line-height:1.6}.composer-toolbar{display:flex;align-items:center;gap:10px}.toolbar-left{min-width:0;display:flex;align-items:center;gap:6px;flex:1}.composer-secondary-controls{display:flex;flex:0 0 auto;align-items:center;gap:10px}.attach-button{width:34px;height:34px;display:grid;flex:0 0 34px;place-items:center;border-radius:50%;background:#f3f5f3;color:#65716a;cursor:pointer}.attach-button input{display:none}.model-select{width:220px}.parameter-select{width:160px}.small-select{width:90px}.quality-control{height:28px;display:flex;flex:0 0 auto;align-items:center;gap:5px;border:1px solid #dce3de;border-radius:5px;padding-left:8px;background:#fff;color:#66736b;font-size:9px}.quality-control>span{white-space:nowrap}.quality-control .el-select{width:64px}.quality-control .el-input__inner{border:0;padding-left:7px}.mode-switch{display:flex;align-items:center;gap:4px;border:1px solid #cbdaf4;border-radius:10px;padding:4px;background:#f1f6ff;box-shadow:0 5px 14px rgba(37,99,235,.09)}.mode-switch button{min-width:82px;height:38px;display:flex;align-items:center;justify-content:center;gap:6px;border:0;border-radius:7px;padding:0 14px;background:transparent;color:#56677f;font-size:12px;font-weight:700;cursor:pointer;transition:.18s ease}.mode-switch button i{font-size:15px}.mode-switch button:hover:not(:disabled){background:#e4edfc;color:#245fc5}.mode-switch button.active{background:#2563eb;color:#fff;box-shadow:0 4px 10px rgba(37,51,43,.1)}.mode-switch button:focus-visible{outline:2px solid #164fae;outline-offset:2px}.mode-switch button:disabled{opacity:.55;cursor:not-allowed}.send-button{width:42px;height:42px;display:grid;flex:0 0 42px;place-items:center;border:0;border-radius:50%;background:#2563eb;color:#fff;font-size:17px}.send-button:disabled{background:#b9c2bc}.attachment-strip{display:flex;gap:8px;margin-bottom:8px}.attachment-strip>div{width:72px;height:58px;position:relative}.attachment-strip img{width:100%;height:100%;display:block;border-radius:6px;object-fit:cover}.attachment-strip span{position:absolute;inset:auto 3px 3px;border-radius:3px;padding:2px;background:rgba(20,28,23,.7);color:#fff;font-size:7px;text-align:center}.attachment-strip button{width:18px;height:18px;position:absolute;top:-5px;right:-5px;border:0;border-radius:50%;padding:0;background:#26322b;color:#fff;font-size:13px;line-height:18px}.composer-error{display:flex;align-items:center;gap:5px;margin-top:8px;color:#c43d3d;font-size:9px}@keyframes typing{0%,100%{opacity:.35;transform:translateY(0)}50%{opacity:1;transform:translateY(-2px)}}@media(max-width:1000px){.parameter-select{display:none}.suggestion-grid{grid-template-columns:1fr}}@media(max-width:767px){.playground-workspace{width:calc(100% - 24px);height:calc(100vh - 88px);min-height:560px;margin:10px auto 0}.playground-head-main{gap:8px}.playground-brand small{display:none}.playground-brand>span{display:none}.playground-brand>div{max-width:120px}.mode-switch{gap:2px;padding:3px}.mode-switch button{min-width:62px;height:36px;padding:0 9px;font-size:11px}.welcome-state{padding:18px}.welcome-state h2{font-size:19px!important}.composer-shell{border-radius:10px;padding:9px}.model-select{width:150px}.small-select{display:none}.quality-control{grid-column:1/-1;width:max-content;min-height:44px;padding-left:12px;font-size:12px}.quality-control .el-select{width:76px}.message-body{max-width:84%}}
.playground-brand>span{background:transparent}
.playground-brand>span img{width:32px;height:32px;display:block;object-fit:contain}
.welcome-state>span{background:transparent}
.welcome-state>span img{width:54px;height:54px;display:block;object-fit:contain}
.message.assistant .message-avatar{background:transparent}
.message-avatar img{width:26px;height:26px;display:block;object-fit:contain}
.message-body>p.streaming::after{width:2px;height:1em;display:inline-block;margin-left:3px;background:#2563eb;content:"";vertical-align:-2px;animation:stream-caret .8s steps(1) infinite}
.playground-workspace{grid-template-rows:auto minmax(0,1fr) auto}
.playground-brand>div{min-width:0}
.playground-brand small{max-width:min(680px,55vw);overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
.system-prompt-preview{max-width:min(720px,58vw);display:flex;align-items:center;gap:8px;border:0;padding:0;background:transparent;color:#7b8780;text-align:left;cursor:pointer}.system-prompt-preview span{min-width:0;overflow:hidden;font-size:9px;line-height:1.5;text-overflow:ellipsis;white-space:nowrap}.system-prompt-preview em{flex:0 0 auto;color:#2563eb;font-size:9px;font-style:normal}.system-prompt-preview:hover span{color:#4f5d55}.system-prompt-preview:disabled{cursor:default}
@keyframes stream-caret{0%,48%{opacity:1}49%,100%{opacity:0}}
.model-context{overflow:hidden;border:1px solid #dfe6e1;border-radius:9px;background:#fbfcfb}.model-context-summary{min-height:52px;display:flex;align-items:center;gap:10px;padding:8px 10px}.model-context-icon{width:32px;height:32px;display:grid;flex:0 0 32px;place-items:center;border-radius:7px;background:#edf4ff;color:#2563eb}.model-context-summary>div{min-width:0;display:grid;flex:1;grid-template-columns:auto 1fr;align-items:baseline;column-gap:8px}.model-context-summary small{grid-column:1/-1;color:#2563eb!important;font-size:8px!important;letter-spacing:.08em}.model-context-summary strong{font-size:11px}.model-context-summary p{overflow:hidden;margin:0!important;color:#7a857e!important;font-size:9px!important;text-overflow:ellipsis;white-space:nowrap}.model-context-summary>button{height:36px;flex:0 0 auto;border:1px solid #dce4de;border-radius:6px;padding:0 10px;background:#fff;color:#5f6c64;font-size:9px;cursor:pointer}.model-context-summary>button:hover{border-color:#9eb7e3;color:#2563eb}.model-context pre{max-height:160px;overflow:auto;margin:0;border-top:1px solid #e4e9e5;padding:12px 14px;background:#f7f9f7;color:#4a574f;font-family:inherit;font-size:10px;line-height:1.7;white-space:pre-wrap}.prompt-reveal-enter-active,.prompt-reveal-leave-active{transition:opacity .18s ease,transform .18s ease}.prompt-reveal-enter,.prompt-reveal-leave-to{opacity:0;transform:translateY(-4px)}
@media(max-width:767px){.model-context-summary p{display:none}.model-context-summary>div{display:flex;flex-direction:column}.model-context-summary>button{padding:0 8px}.model-context pre{max-height:120px}}
.image-generating-status{min-width:300px;display:flex;align-items:center;gap:14px;overflow:hidden;position:relative;border:1px solid #ccdcf8;border-radius:14px;padding:16px 18px;background:linear-gradient(135deg,#f8fbff 0%,#eef4ff 100%);color:#2563eb;box-shadow:0 10px 28px rgba(37,99,235,.09)}
.image-generating-status::after{width:45%;position:absolute;inset:0 auto 0 -55%;background:linear-gradient(100deg,transparent,rgba(255,255,255,.8),transparent);content:"";animation:generation-shimmer 2.6s ease-in-out infinite}
.generation-visual{width:48px;height:48px;display:grid;flex:0 0 48px;position:relative;place-items:center;border-radius:50%;background:rgba(255,255,255,.92);box-shadow:0 5px 16px rgba(37,99,235,.14)}
.generation-visual>i:last-child{font-size:19px}
.generation-orbit{position:absolute;inset:4px;border:2px solid rgba(37,99,235,.14);border-top-color:#2563eb;border-right-color:#7da4ea;border-radius:50%;animation:generation-spin 1.2s cubic-bezier(.55,.15,.45,.85) infinite}
.generation-copy{display:flex;position:relative;z-index:1;flex-direction:column;gap:4px}
.generation-copy strong{color:#244a89;font-size:12px;line-height:1.4}
.generation-copy small{color:#63789c!important;font-size:10px!important;line-height:1.5}
.generation-copy em{display:flex;align-items:center;gap:5px;color:#8190aa;font-size:9px;font-style:normal;line-height:1.45}
.generation-copy em>i{width:6px;height:6px;flex:0 0 6px;border-radius:50%;background:#44a06b;box-shadow:0 0 0 3px rgba(68,160,107,.12);animation:generation-pulse 1.8s ease-in-out infinite}
.message-meta{display:flex;align-items:center;flex-wrap:wrap;gap:8px;margin-top:2px}
.message-meta-item{min-height:30px;display:inline-flex;align-items:center;gap:6px;border:1px solid #d8e3f5;border-radius:8px;padding:5px 9px;background:#f7faff;color:#52647f;font-size:10px;line-height:1.35;box-shadow:0 2px 7px rgba(37,99,235,.05)}
.message-meta-item>i{color:#2563eb;font-size:12px}
.message-meta-label{color:#71809a;font-size:9px}
.message-meta-item strong{color:#244a89;font-size:11px;font-weight:700}
.message-meta-item time{color:#34445d;font-size:11px;font-weight:600;font-variant-numeric:tabular-nums}
.message.user .message-meta{justify-content:flex-end}
.message.user .message-meta-item{border-color:#dfe5e1;background:#fafbfa;color:#647068;box-shadow:none}
.message.user .message-meta-item>i{color:#647068}
.message.user .message-meta-label{color:#7c8780}
.message.user .message-meta-item time{color:#4c5851}
.composer-shell{position:relative}
.composer-shell.drag-active{border-color:#7d9fe6;background:#f8fbff;box-shadow:0 0 0 3px rgba(37,99,235,.1),0 14px 38px rgba(30,43,35,.1)}
.composer-drop-overlay{position:absolute;z-index:6;inset:5px;display:flex;align-items:center;justify-content:center;gap:8px;border:1px dashed #7d9fe6;border-radius:10px;background:rgba(244,248,255,.96);color:#2563eb;font-size:12px;font-weight:700;pointer-events:none}
.composer-drop-overlay i{font-size:19px}
.composer-shell.locked{min-height:118px}
.composer-lock{position:absolute;z-index:5;inset:0;display:flex;align-items:center;justify-content:center;gap:13px;border:1px solid rgba(204,220,248,.75);border-radius:inherit;background:rgba(248,251,255,.96);color:#2563eb;cursor:wait;backdrop-filter:blur(6px)}
.generation-visual.compact{width:42px;height:42px;flex-basis:42px}
@keyframes generation-spin{to{transform:rotate(360deg)}}
@keyframes generation-shimmer{0%,15%{transform:translateX(0)}70%,100%{transform:translateX(360%)}}
@keyframes generation-pulse{0%,100%{opacity:.55;transform:scale(.88)}50%{opacity:1;transform:scale(1)}}
@media(max-width:767px){.image-generating-status{min-width:0;padding:14px}.generation-visual{width:42px;height:42px;flex-basis:42px}.generation-copy strong{font-size:11px}.generation-copy small{font-size:9px!important}}
.quick-prompt-row{display:flex;align-items:center;gap:6px;margin-bottom:2px}
.quick-prompt-row>span{display:flex;flex:0 0 auto;align-items:center;gap:3px;color:#69766e;font-size:9px;font-weight:600;white-space:nowrap}
.quick-prompt-row>span i{color:#2563eb;font-size:10px}
.quick-prompt-row>div{min-width:0;display:flex;flex:1;gap:4px;overflow-x:auto;padding:0 1px 2px;scrollbar-color:#c8d4cd transparent;scrollbar-width:thin}
.quick-prompt-row button{min-height:24px;flex:0 0 auto;border:1px solid #dce5df;border-radius:6px;padding:0 7px;background:#f8faf8;color:#4d5b52;font-size:9px;line-height:1.2;cursor:pointer;touch-action:manipulation;transition:border-color .18s ease,background-color .18s ease,color .18s ease}
.quick-prompt-row button:hover{border-color:#9db6e4;background:#f1f6ff;color:#245fc5}
.quick-prompt-row button:active{background:#e7effd}
.quick-prompt-row button:focus-visible{outline:2px solid #2563eb;outline-offset:2px}
.quick-prompt-row button:disabled{opacity:.45;cursor:not-allowed}
@media(max-width:767px){.quick-prompt-row{align-items:flex-start;flex-direction:column;gap:2px}.quick-prompt-row>div{width:100%}.quick-prompt-row button{min-height:28px;font-size:10px}}
@media(prefers-reduced-motion:reduce){.quick-prompt-row button{transition:none}.generation-orbit,.image-generating-status::after,.generation-copy em>i{animation:none}}

.playground-head{align-items:flex-start}
.playground-head-main{flex:1;align-items:center;overflow:hidden}
.mode-selector{width:184px;flex:0 0 184px;border:1px solid #b9cdf2;border-radius:12px;padding:7px;background:linear-gradient(145deg,#f7faff,#eaf2ff);box-shadow:0 7px 18px rgba(37,99,235,.14)}
.mode-selector-label{display:flex;align-items:center;gap:4px;margin:0 2px 5px;color:#2458a9;font-size:10px;font-weight:700}
.mode-selector-label i{font-size:12px}
.mode-selector .mode-switch{width:100%;box-sizing:border-box;border:0;padding:3px;background:#dce9ff;box-shadow:none}
.mode-selector .mode-switch button{min-width:0;height:38px;flex:1;padding:0 10px;font-size:12px}
.playground-brand{min-width:0;flex:1}
.playground-brand>div{min-width:0}
.system-prompt-preview{width:min(620px,48vw);max-width:100%}
@media(max-width:767px){
  .playground-head-main{align-items:flex-start}
  .mode-selector{width:142px;flex-basis:142px;padding:5px}
  .mode-selector-label{margin-bottom:3px;font-size:9px}
  .mode-selector .mode-switch button{height:34px;padding:0 6px;font-size:10px}
  .playground-brand{padding-top:7px}
  .playground-brand>div{max-width:100px}
}

.mode-toolbar{width:fit-content;max-width:100%;min-height:0;align-self:start;justify-self:start;display:flex;align-items:center;gap:12px;border:0;border-radius:0;padding:0;background:transparent;box-shadow:none}
.mode-toolbar-copy{width:150px;flex:0 0 150px;padding-left:3px}
.mode-toolbar-copy strong{display:block;color:#344139;font-size:11px}
.mode-toolbar-copy span{display:block;margin-top:2px;color:#929b96;font-size:8px}
.mode-toolbar .mode-switch{width:390px;gap:5px;border:0;padding:0;background:transparent;box-shadow:none}
.mode-toolbar .mode-switch button{min-width:0;height:40px;flex:1;justify-content:flex-start;gap:8px;position:relative;border:1px solid transparent;border-radius:7px;padding:0 10px;background:#f3f6f4;color:#59665e;text-align:left;box-shadow:none}
.mode-toolbar .mode-switch button>i{width:26px;height:26px;display:grid;flex:0 0 26px;place-items:center;border-radius:6px;background:#e5ebe7;color:#69766e;font-size:13px}
.mode-toolbar .mode-switch button>span{min-width:0;display:flex;flex-direction:column;gap:2px}
.mode-toolbar .mode-switch button strong{font-size:11px}
.mode-toolbar .mode-switch button small{color:#87918b;font-size:8px;font-weight:600;white-space:nowrap}
.mode-toolbar .mode-switch button>b{margin-left:auto;border-radius:9px;padding:2px 5px;background:#fff;color:#2563eb;font-size:7px}
.mode-toolbar .mode-switch button:hover:not(:disabled){border-color:#a9bfe7;background:#f3f7ff;color:#245fc5}
.mode-toolbar .mode-switch button.active{border-color:#b9cef2;background:#eaf2ff;color:#2057b3;box-shadow:inset 0 0 0 1px rgba(37,99,235,.04)}
.mode-toolbar .mode-switch button.active>i{background:#d7e6ff;color:#2563eb}
.mode-toolbar .mode-switch button.active small{color:#5577ae}
@media(max-width:767px){
  .mode-toolbar{width:100%;align-items:stretch;flex-direction:column;gap:5px;padding:0}
  .mode-toolbar-copy{width:auto;padding-left:2px}
  .mode-toolbar-copy span{display:none}
  .mode-toolbar .mode-switch{width:100%;gap:6px}
  .mode-toolbar .mode-switch button{height:38px;padding:0 8px}
  .mode-toolbar .mode-switch button>i{width:24px;height:24px;flex-basis:24px}
  .mode-toolbar .mode-switch button small,.mode-toolbar .mode-switch button>b{display:none}
}
.compact-mode-switch{height:34px;display:flex;flex:0 0 auto;align-items:center;border:1px solid #dfe5e1;border-radius:8px;padding:2px;background:#f5f7f5}
.compact-mode-switch button{width:58px;height:28px;display:flex;align-items:center;justify-content:center;gap:5px;border:0;border-radius:6px;padding:0 7px;background:transparent;color:#7a867f;font-size:11px;cursor:pointer;transition:.18s}
.compact-mode-switch button i{font-size:13px}
.compact-mode-switch button span{font-weight:600;white-space:nowrap}
.compact-mode-switch button:hover:not(:disabled){background:#e9eeeb;color:#4e5e55}
.compact-mode-switch button.active{background:#fff;color:#2563eb;box-shadow:0 1px 4px rgba(32,49,39,.12)}
.compact-mode-switch button:focus-visible{outline:2px solid #2563eb;outline-offset:1px}
.compact-mode-switch button:disabled{opacity:.45;cursor:not-allowed}
.head-actions{display:flex;flex:0 0 auto;gap:7px}
.head-actions button{height:34px;border:1px solid #dce3de;border-radius:7px;padding:0 11px;background:#fff;color:#5f6c64;font-size:9px;cursor:pointer}
.head-actions button:hover:not(:disabled){border-color:#9eb7e3;color:#2563eb}
.head-actions button:disabled{opacity:.5;cursor:not-allowed}
.history-layer{position:fixed;z-index:2800;inset:0;background:rgba(26,34,29,.28)}
.conversation-history{width:min(380px,92vw);height:100%;display:flex;position:absolute;top:0;right:0;box-sizing:border-box;flex-direction:column;gap:12px;padding:20px;background:#f8faf8;box-shadow:-18px 0 50px rgba(24,35,28,.16)}
.conversation-history>header{display:flex;align-items:center;justify-content:space-between}
.conversation-history>header>div{display:flex;flex-direction:column;gap:3px}
.conversation-history>header strong{font-size:16px}
.conversation-history>header small{color:#8a948e;font-size:9px}
.conversation-history>header>button{width:32px;height:32px;border:0;border-radius:7px;background:#eef1ef;color:#66736b;font-size:20px;cursor:pointer}
.history-new{height:38px;border:1px solid #b9cef2;border-radius:8px;background:#edf4ff;color:#245fc5;font-size:11px;cursor:pointer}
.history-state{display:grid;min-height:140px;place-items:center;color:#8a948e;font-size:10px}
.history-list{min-height:0;display:flex;overflow-y:auto;flex:1;flex-direction:column;gap:6px}
.history-list article{display:flex;align-items:center;gap:8px;border:1px solid transparent;border-radius:9px;padding:10px;background:#fff;cursor:pointer}
.history-list article:hover{border-color:#d7e1dc}
.history-list article.active{border-color:#b9cef2;background:#edf4ff}
.history-list article>div{min-width:0;display:flex;flex:1;flex-direction:column;gap:3px}
.history-list article strong,.history-list article p{overflow:hidden;margin:0;text-overflow:ellipsis;white-space:nowrap}
.history-list article strong{color:#344139;font-size:11px}
.history-list article p{color:#7f8a84;font-size:9px}
.history-list article small{color:#9ba39e;font-size:8px}
.history-list article>button{width:28px;height:28px;flex:0 0 28px;border:0;border-radius:6px;background:transparent;color:#a4aca7;cursor:pointer}
.history-list article>button:hover{background:#fff;color:#c33d3d}
.history-fade-enter-active,.history-fade-leave-active{transition:opacity .18s ease}
.history-fade-enter-active .conversation-history,.history-fade-leave-active .conversation-history{transition:transform .18s ease}
.history-fade-enter,.history-fade-leave-to{opacity:0}
.history-fade-enter .conversation-history,.history-fade-leave-to .conversation-history{transform:translateX(100%)}
.conversation-loading{position:absolute;z-index:3;top:8px;left:50%;border:1px solid #e0e6e2;border-radius:16px;padding:6px 10px;background:#fff;color:#718078;font-size:9px;transform:translateX(-50%);box-shadow:0 5px 16px rgba(30,43,35,.08)}
.message-stage{position:relative}
@media(max-width:767px){.head-actions button{width:34px;padding:0;font-size:0}.head-actions button i{font-size:13px}.conversation-history{padding:16px}}

/* Mobile H5: keep the conversation and primary composer controls in view. */
.visually-hidden{width:1px;height:1px;overflow:hidden;position:absolute;clip:rect(0 0 0 0);clip-path:inset(50%);white-space:nowrap}
@media(max-width:767px){
  .playground-workspace{
    width:calc(100% - 16px);
    height:calc(100svh - 76px - env(safe-area-inset-top));
    height:calc(100dvh - 76px - env(safe-area-inset-top));
    min-height:0;
    max-height:calc(100dvh - 76px - env(safe-area-inset-top));
    gap:8px;
    margin:8px auto 0;
    padding-bottom:env(safe-area-inset-bottom);
    overflow:hidden;
    box-sizing:border-box;
  }
  .playground-head{min-height:44px;align-items:center;gap:8px}
  .playground-head-main{min-width:0;align-items:center}
  .playground-brand{min-width:0;padding:0}
  .playground-brand>div{max-width:min(44vw,160px)}
  .playground-brand strong{overflow:hidden;font-size:14px;text-overflow:ellipsis;white-space:nowrap}
  .system-prompt-preview{display:none}
  .head-actions{gap:8px}
  .head-actions button{width:44px;height:44px;padding:0;font-size:0!important;touch-action:manipulation}
  .head-actions button i{font-size:16px}

  .message-stage{
    overscroll-behavior:contain;
    -webkit-overflow-scrolling:touch;
    scrollbar-width:thin;
  }
  .welcome-state{min-height:100%;justify-content:center;padding:16px 8px}
  .welcome-state>span{width:52px;height:52px;border-radius:16px;font-size:23px}
  .welcome-state h2{margin:12px 0 4px!important;font-size:20px!important}
  .welcome-state p{max-width:300px;font-size:13px!important}
  .suggestion-grid{gap:8px;margin-top:16px}
  .suggestion-grid button{min-height:60px;gap:10px;padding:10px 12px;touch-action:manipulation}
  .suggestion-grid strong{font-size:14px}
  .suggestion-grid small{font-size:11px!important}
  .message-list{padding:8px 0 12px}
  .message{gap:8px;margin-bottom:16px}
  .message-avatar{width:30px;height:30px;flex-basis:30px}
  .message-body{max-width:calc(100% - 38px);gap:6px}
  .message-content{max-width:100%;box-sizing:border-box;padding:10px 12px;font-size:14px;line-height:1.65;overflow-wrap:anywhere}
  .message-images{max-width:100%}
  .message-images button{width:96px;height:72px}
  .generated-image{width:100%;max-width:100%}
  .generated-image img{max-height:58dvh}
  .generated-image button{right:8px;bottom:8px;min-height:44px;padding:0 12px;font-size:12px}
  .image-generating-status{width:100%;box-sizing:border-box}
  .generation-copy{min-width:0}

  .composer-shell{
    min-width:0;
    border-radius:10px;
    padding:8px 8px calc(8px + env(safe-area-inset-bottom));
    box-shadow:0 8px 24px rgba(30,43,35,.12);
  }
  .quick-prompt-row{display:none}
  .composer-shell textarea{
    min-height:48px;
    max-height:96px;
    box-sizing:border-box;
    padding:4px 4px 6px!important;
    font-size:16px!important;
    line-height:1.55;
  }
  .composer-toolbar{
    display:grid;
    grid-template-columns:minmax(0,1fr);
    align-items:center;
    gap:8px;
  }
  .toolbar-left{width:100%;display:grid;grid-template-columns:44px 92px minmax(0,1fr);align-items:center;gap:6px}
  .composer-secondary-controls{width:100%;min-width:0;display:flex;align-items:center;gap:6px}
  .composer-toolbar.mode-chat .composer-secondary-controls{justify-content:flex-end}
  .attach-button{width:44px;height:44px;flex-basis:44px;touch-action:manipulation}
  .compact-mode-switch{width:92px;height:44px;box-sizing:border-box}
  .compact-mode-switch button{width:48px;height:38px;padding:0 4px;touch-action:manipulation}
  .model-badge{width:100%;min-width:0;overflow:hidden;box-sizing:border-box;justify-content:center;text-overflow:ellipsis}
  .composer-quality-selector{width:auto;min-width:0;flex:1 1 auto}
  .model-select{width:100%;min-width:0}
  .model-select .el-input,.model-select .el-input__inner{width:100%;min-width:0}
  .model-select .el-input__inner{padding-right:24px;padding-left:8px}
  .small-select{display:none!important}
  .send-button{width:44px;height:44px;flex-basis:44px;touch-action:manipulation}
  .attachment-strip{max-width:100%;overflow-x:auto;overflow-y:visible;padding:8px 4px 4px}
  .attachment-strip>div{flex:0 0 72px}
  .attachment-strip button{
    width:44px;
    height:44px;
    top:-12px;
    right:-12px;
    display:grid;
    place-items:center;
    background:transparent;
    color:transparent;
  }
  .attachment-strip button::after{
    width:24px;
    height:24px;
    display:grid;
    place-items:center;
    border-radius:50%;
    background:#26322b;
    color:#fff;
    content:"×";
    font-size:16px;
  }

  .conversation-history{
    width:100%;
    max-width:none;
    gap:12px;
    padding:calc(16px + env(safe-area-inset-top)) 16px calc(16px + env(safe-area-inset-bottom));
  }
  .conversation-history>header>button{width:44px;height:44px}
  .history-new{min-height:44px}
  .history-list article{min-height:64px;box-sizing:border-box}
  .history-list article>button{width:44px;height:44px;flex-basis:44px}
}
@media(max-width:390px){
  .playground-brand>div{max-width:104px}
  .compact-mode-switch{width:92px}
  .compact-mode-switch button{width:42px}
  .compact-mode-switch button span{display:none}
  .welcome-state{padding-inline:4px}
}
@media(max-height:560px) and (max-width:900px){
  .playground-workspace{
    height:calc(100dvh - 92px - env(safe-area-inset-top));
    min-height:0;
    max-height:calc(100dvh - 92px - env(safe-area-inset-top));
    gap:6px;
    margin-top:6px;
  }
  .head-actions button{height:44px;min-height:44px;padding-inline:12px;font-size:12px}
  .welcome-state{padding:6px}
  .welcome-state>span,.welcome-state p{display:none}
  .welcome-state h2{margin:4px 0!important;font-size:17px!important}
  .suggestion-grid{grid-template-columns:repeat(2,minmax(0,1fr));margin-top:8px}
  .suggestion-grid button{min-height:48px;padding:6px 8px}
  .suggestion-grid small{display:none}
  .quick-prompt-row{display:none!important}
  .composer-shell{padding-top:6px}
  .composer-shell textarea{min-height:44px;max-height:60px}
  .attach-button{width:44px;height:44px;flex-basis:44px}
  .compact-mode-switch{height:48px}
  .compact-mode-switch button{height:44px;min-height:44px}
  .model-select .el-input__inner,.small-select .el-input__inner{height:44px;min-height:44px;font-size:14px}
  .send-button{width:44px;height:44px;min-height:44px;flex-basis:44px}
}
.generated-image-media{position:relative;background:#f4f6f5}
.generated-image-actions{right:12px;bottom:12px;gap:8px}
.generated-image-actions button{
  width:96px;
  min-width:96px;
  height:38px;
  min-height:38px;
  display:inline-flex;
  align-items:center;
  justify-content:center;
  gap:6px;
  box-sizing:border-box;
  border:1px solid rgba(255,255,255,.14);
  border-radius:7px;
  padding:0 10px;
  background:rgba(22,31,26,.88);
  box-shadow:0 6px 18px rgba(14,22,17,.22);
  font-size:11px;
  font-weight:700;
  line-height:1;
  cursor:pointer;
  backdrop-filter:blur(8px);
  transition:background-color .18s ease,border-color .18s ease,box-shadow .18s ease;
}
.generated-image-actions button:hover:not(:disabled){background:rgba(22,31,26,.98);border-color:rgba(255,255,255,.28)}
.generated-image-actions button:focus-visible{outline:3px solid rgba(116,159,241,.72);outline-offset:3px}
.generated-image-actions button i{font-size:14px}
.generated-image-actions .generated-image-edit-button{
  width:96px;
  min-width:96px;
  height:38px;
  min-height:38px;
  display:inline-flex;
  align-items:center;
  justify-content:center;
  gap:6px;
  box-sizing:border-box;
  border:1px solid #78a3f4;
  border-radius:7px;
  padding:0 10px;
  background:#2563eb;
  color:#fff;
  font-size:11px;
  font-weight:700;
  cursor:pointer;
  box-shadow:0 6px 18px rgba(37,99,235,.34);
  transition:background-color .18s ease,border-color .18s ease,box-shadow .18s ease;
}
.generated-image-actions .generated-image-edit-button:hover:not(:disabled){border-color:#78a3f4;background:#2563eb;box-shadow:0 6px 18px rgba(37,99,235,.34)}
.generated-image-actions .generated-image-edit-button:focus-visible{outline:3px solid rgba(116,159,241,.72);outline-offset:3px}
.generated-image-actions .generated-image-edit-button:disabled{border-color:#94a8c9;background:#8397b8;color:#e8edf5;cursor:not-allowed;box-shadow:none}
.generated-image-actions .generated-image-edit-button i{font-size:14px}
.generated-image-actions .generated-image-workflow-button{
  width:112px;
  min-width:112px;
  height:38px;
  min-height:38px;
  justify-content:center;
  gap:6px;
  box-sizing:border-box;
  border:1px solid #b9d0fb;
  border-radius:7px;
  padding:0 10px;
  margin-left:0;
  background:rgba(35,79,159,.92);
  color:#fff !important;
  font-size:11px;
  font-weight:700;
  line-height:1;
  text-decoration:none;
  box-shadow:0 6px 18px rgba(14,22,17,.22);
  backdrop-filter:blur(8px);
  transition:background-color .18s ease,border-color .18s ease,box-shadow .18s ease;
}
.generated-image-actions .generated-image-workflow-button:hover:not([aria-disabled='true']){border-color:#d7e5ff;background:#234f9f;color:#fff !important;text-decoration:none}
.generated-image-actions .generated-image-workflow-button[aria-disabled='true']{border-color:#94a8c9;background:#8397b8;color:#e8edf5 !important;box-shadow:none}

@media(max-width:767px){
  .generated-image-actions{right:8px;bottom:8px;gap:8px}
  .generated-image-actions button,.generated-image-actions .generated-image-edit-button,.generated-image-actions .generated-image-workflow-button{width:104px;min-width:104px;height:44px;min-height:44px;padding:0 8px;font-size:11px}
}

@media(max-width:430px){
  .generated-image-actions button,.generated-image-actions .generated-image-edit-button{width:44px;min-width:44px;height:44px;min-height:44px;padding:0;font-size:0}
  .generated-image-actions button i{font-size:15px}
}

@media(prefers-reduced-motion:reduce){.generated-image-actions .generated-image-edit-button{transition:none}}

/* Generation quality: keep the selected tier and its cost readable as one control. */
.quality-control{
  height:36px;
  display:flex;
  flex:0 0 auto;
  align-items:center;
  gap:7px;
  box-sizing:border-box;
  border:1px solid #d9e4f7;
  border-radius:10px;
  padding:3px 4px 3px 9px;
  background:linear-gradient(135deg,#f8fbff 0%,#f2f6ff 100%);
  color:#52647f;
  box-shadow:0 3px 10px rgba(37,99,235,.06);
  transition:border-color .18s ease,box-shadow .18s ease,background-color .18s ease;
}
.quality-control:hover{border-color:#aec4ed}
.quality-control:focus-within{border-color:#7da3e7;box-shadow:0 0 0 3px rgba(37,99,235,.1)}
.quality-control-title{display:flex;align-items:center;gap:5px;color:#53647d;font-size:10px;font-weight:600;white-space:nowrap}
.quality-control-title i{color:#2563eb;font-size:13px}
@media(max-width:767px){
  .quality-control{width:100%;min-height:44px;height:44px;grid-column:1/-1;padding:4px 5px 4px 12px}
  .quality-control-title{font-size:12px}
  .quality-control-title i{font-size:15px}
}
@media(prefers-reduced-motion:reduce){.quality-control{transition:none}}
.prompt-optimize-button{
  min-width:118px;
  min-height:44px;
  display:inline-flex;
  flex:0 0 auto;
  align-items:center;
  justify-content:center;
  gap:7px;
  border:1px solid #b9cdf2;
  border-radius:9px;
  padding:0 14px;
  background:linear-gradient(135deg,#f8fbff 0%,#edf4ff 100%);
  color:#245fc5;
  font-size:11px;
  font-weight:700;
  white-space:nowrap;
  cursor:pointer;
  box-shadow:0 3px 10px rgba(37,99,235,.08);
  transition:transform .18s ease,border-color .18s ease,background-color .18s ease,box-shadow .18s ease;
}
.prompt-optimize-button:hover:not(:disabled){border-color:#7da3e7;background:#e7f0ff;box-shadow:0 6px 16px rgba(37,99,235,.14);transform:translateY(-1px)}
.prompt-optimize-button:focus-visible{outline:2px solid #245fc5;outline-offset:2px}
.prompt-optimize-button:disabled{border-color:#dce4ed;background:#f5f7f8;color:#9ca9b6;cursor:not-allowed;box-shadow:none}
.prompt-optimize-button i{font-size:15px}
.prompt-optimize-label-mobile{display:none}
.optimization-feedback{display:flex;align-items:center;justify-content:flex-end;gap:10px;margin-top:8px;color:#557264;font-size:10px}
.optimization-feedback span{display:inline-flex;align-items:center;gap:5px}
.optimization-feedback span i{color:#3b8a64;font-size:13px}
.optimization-feedback button{min-height:32px;border:0;border-radius:6px;padding:0 8px;background:transparent;color:#2563eb;font-size:10px;cursor:pointer}
.optimization-feedback button:hover:not(:disabled){background:#edf4ff}
.optimization-feedback button:focus-visible{outline:2px solid #245fc5;outline-offset:1px}
.optimization-feedback button:disabled{color:#a8b0ac;cursor:not-allowed}
@media(max-width:767px){
  .prompt-optimize-button{width:119px;min-width:119px;height:44px;gap:6px;margin-left:auto;padding:0 8px;border-radius:11px}
  .prompt-optimize-label-full{display:none}
  .prompt-optimize-label-mobile{display:inline}
  .optimization-feedback{justify-content:space-between;gap:6px;font-size:11px}
  .optimization-feedback button{min-height:44px;font-size:11px}
}
@media(max-width:340px){
  .prompt-optimize-button{width:44px;min-width:44px;padding:0;border-radius:50%}
  .prompt-optimize-label-mobile{display:none}
}
@media(prefers-reduced-motion:reduce){.prompt-optimize-button{transition:none}.prompt-optimize-button:hover:not(:disabled){transform:none}}
.model-badge{min-height:34px;display:inline-flex;align-items:center;border:1px solid #dce3de;border-radius:6px;padding:0 10px;background:#f7f9f8;color:#526159;font-size:10px;font-weight:700;white-space:nowrap}

/* Mask inpainting editor: the red overlay is visual only; submission exports an alpha PNG. */
.generated-image-actions{max-width:calc(100% - 18px);flex-wrap:wrap;justify-content:flex-end}
.generated-image-actions button:disabled{opacity:.5;cursor:not-allowed}
.mask-editor-layer{position:fixed;z-index:3200;inset:0;display:grid;place-items:center;box-sizing:border-box;padding:24px;background:rgba(17,24,20,.62);backdrop-filter:blur(5px)}
.mask-editor-dialog{width:min(1180px,100%);max-height:calc(100dvh - 48px);display:grid;grid-template-rows:auto minmax(0,1fr) auto;overflow:hidden;border:1px solid rgba(255,255,255,.55);border-radius:18px;background:#fff;box-shadow:0 28px 90px rgba(9,16,12,.34)}
.mask-editor-head{min-height:72px;display:flex;align-items:center;justify-content:space-between;gap:18px;box-sizing:border-box;border-bottom:1px solid #e4e9e6;padding:12px 18px;background:#fbfcfb}
.mask-editor-head>div{min-width:0;display:flex;align-items:center;gap:12px}
.mask-editor-icon{width:42px;height:42px;display:grid;flex:0 0 42px;place-items:center;border-radius:11px;background:#eaf2ff;color:#2563eb;font-size:18px}
.mask-editor-head h2{margin:0!important;color:#26342c;font-size:17px!important;line-height:1.35}
.mask-editor-head p{margin:3px 0 0!important;color:#758179!important;font-size:11px!important;line-height:1.5}
.mask-editor-head>button{width:44px;height:44px;display:grid;flex:0 0 44px;place-items:center;border:0;border-radius:10px;background:#eef1ef;color:#66736b;font-size:18px;cursor:pointer}
.mask-editor-head>button:hover:not(:disabled){background:#e5eae7;color:#26342c}
.mask-editor-head>button:focus-visible{outline:2px solid #2563eb;outline-offset:2px}
.mask-editor-head>button:disabled{opacity:.45;cursor:not-allowed}
.mask-editor-body{min-height:0;display:grid;grid-template-columns:minmax(0,1fr) 320px}
.mask-editor-workbench{min-width:0;min-height:420px;display:grid;place-items:center;overflow:hidden;padding:18px;background:linear-gradient(145deg,#222a25,#101612)}
.mask-editor-loading,.mask-editor-load-error{display:flex;align-items:center;flex-direction:column;gap:7px;color:#dce5df;text-align:center}
.mask-editor-loading>i,.mask-editor-load-error>i{margin-bottom:6px;color:#8db1f2;font-size:29px}
.mask-editor-loading strong,.mask-editor-load-error strong{font-size:14px}
.mask-editor-loading span,.mask-editor-load-error span{max-width:340px;color:#9eaba3;font-size:11px;line-height:1.6}
.mask-canvas-wrap{max-width:100%;display:flex;align-items:center;justify-content:center;flex-direction:column;gap:8px}
.mask-canvas-frame{max-width:100%;display:inline-block;overflow:hidden;position:relative;border-radius:7px;background:#090d0b;box-shadow:0 14px 40px rgba(0,0,0,.28);line-height:0;user-select:none}
.mask-canvas-frame>img{width:auto;height:auto;max-width:100%;max-height:min(64dvh,720px);display:block;object-fit:contain;pointer-events:none}
/* Keep the full-resolution canvas out of layout; it only overlays the fitted preview. */
.mask-paint-canvas{position:absolute;z-index:1;inset:0;width:100%;height:100%;display:block;opacity:.52;cursor:crosshair;touch-action:none}
.mask-paint-canvas.drawing{cursor:none}
.mask-canvas-size{color:#98a59d;font-size:10px;font-variant-numeric:tabular-nums}
.mask-editor-controls{min-height:0;overflow-y:auto;box-sizing:border-box;border-left:1px solid #e1e7e3;padding:18px;background:#fff}
.mask-tool-section,.mask-prompt-section{display:flex;flex-direction:column}
.mask-control-title{display:flex;align-items:center;justify-content:space-between;gap:10px;margin-bottom:12px;color:#46544b;font-size:12px}
.mask-control-title span{display:inline-flex;align-items:center;gap:6px;font-weight:700}
.mask-control-title span i{color:#2563eb;font-size:15px}
.mask-control-title strong{border-radius:12px;padding:3px 8px;background:#edf4ff;color:#245fc5;font-size:10px;font-variant-numeric:tabular-nums}
.mask-tool-section input[type=range]{width:100%;height:22px;margin:0;accent-color:#2563eb;cursor:pointer}
.mask-tool-section input[type=range]:disabled{opacity:.5;cursor:not-allowed}
.mask-brush-scale{display:flex;align-items:center;justify-content:space-between;margin-top:4px;padding:0 6px;color:#9aa49e}
.mask-brush-scale span{display:block;border:1px solid #a9b4ad;border-radius:50%}
.mask-brush-scale span:nth-child(1){width:5px;height:5px}.mask-brush-scale span:nth-child(2){width:10px;height:10px}.mask-brush-scale span:nth-child(3){width:16px;height:16px}
.mask-history-actions{display:grid;grid-template-columns:1fr 1fr;gap:8px;margin:18px 0;padding-bottom:18px;border-bottom:1px solid #e5e9e6}
.mask-history-actions button{min-height:44px;border:1px solid #dce4df;border-radius:9px;background:#f8faf8;color:#526159;font-size:11px;cursor:pointer}
.mask-history-actions button:hover:not(:disabled){border-color:#adc2e8;background:#f2f6ff;color:#245fc5}
.mask-history-actions button:focus-visible{outline:2px solid #2563eb;outline-offset:1px}
.mask-history-actions button:disabled{opacity:.42;cursor:not-allowed}
.mask-prompt-section{position:relative}
.mask-prompt-section label{margin-bottom:8px;color:#344139;font-size:12px;font-weight:700}
.mask-prompt-input{position:relative}
.mask-prompt-section textarea{width:100%;min-height:116px;resize:vertical;box-sizing:border-box;border:1px solid #dce3de;border-radius:10px;padding:11px 12px 24px;outline:none;color:#344139;font-family:inherit;font-size:13px;line-height:1.65}
.mask-prompt-section textarea:focus{border-color:#7da3e7;box-shadow:0 0 0 3px rgba(37,99,235,.1)}
.mask-prompt-input small{position:absolute;right:9px;bottom:7px;color:#98a29c;font-size:9px;font-variant-numeric:tabular-nums}
.mask-edit-tip{display:flex;align-items:flex-start;gap:7px;margin-top:13px;border-radius:9px;padding:10px;background:#f4f7f5;color:#69766e;font-size:10px;line-height:1.55}
.mask-edit-tip i{margin-top:2px;color:#2563eb}
.mask-editor-error{display:flex;align-items:flex-start;gap:6px;margin:12px 0 0!important;border-radius:8px;padding:9px;background:#fff2f2;color:#bf3636!important;font-size:10px!important;line-height:1.5}
.mask-editor-footer{min-height:72px;display:flex;align-items:center;justify-content:space-between;gap:18px;box-sizing:border-box;border-top:1px solid #e3e8e5;padding:12px 18px;background:#fbfcfb}
.mask-editor-footer>span{display:inline-flex;align-items:center;gap:6px;color:#8a948e;font-size:11px}
.mask-editor-footer>span.ready{color:#347556}
.mask-editor-footer>div{display:flex;align-items:center;gap:9px}
.mask-editor-footer button{min-height:44px;border-radius:9px;padding:0 17px;font-size:12px;font-weight:700;cursor:pointer}
.mask-cancel-button{border:1px solid #d9e1dc;background:#fff;color:#617068}
.mask-submit-button{min-width:154px;border:1px solid #2563eb;background:#2563eb;color:#fff;box-shadow:0 7px 18px rgba(37,99,235,.2)}
.mask-submit-button:hover:not(:disabled){background:#1f57ca}
.mask-editor-footer button:focus-visible{outline:2px solid #164fae;outline-offset:2px}
.mask-editor-footer button:disabled{border-color:#d8dedb;background:#e9edeb;color:#9ca5a0;cursor:not-allowed;box-shadow:none}
@media(max-width:820px){
  .mask-editor-layer{align-items:stretch;padding:0;background:#fff}
  .mask-editor-dialog{width:100%;max-height:100dvh;border:0;border-radius:0;box-shadow:none}
  .mask-editor-head{min-height:68px;padding:10px 12px}
  .mask-editor-head p{font-size:10px!important}
  .mask-editor-body{display:flex;overflow-y:auto;flex-direction:column}
  .mask-editor-workbench{min-height:300px;flex:0 0 auto;padding:10px}
  .mask-canvas-frame>img{max-height:46dvh}
  .mask-editor-controls{overflow:visible;border-top:1px solid #e1e7e3;border-left:0;padding:14px}
  .mask-editor-footer{min-height:84px;gap:8px;padding:10px 12px calc(10px + env(safe-area-inset-bottom))}
  .mask-editor-footer>span{max-width:42%;font-size:10px;line-height:1.35}
  .mask-editor-footer button{padding:0 12px}
  .mask-submit-button{min-width:142px}
}
@media(max-width:480px){
  .mask-editor-icon{display:none}
  .mask-editor-head h2{font-size:15px!important}
  .mask-editor-footer>span{display:none}
  .mask-editor-footer>div{width:100%}
  .mask-editor-footer button{flex:1}
  .mask-submit-button{min-width:0;flex:2!important}
}
@media(prefers-reduced-motion:reduce){.mask-editor-layer{backdrop-filter:none}}
</style>

<style>
.playground-model-menu.el-select-dropdown{max-width:min(430px,calc(100vw - 24px))}
.playground-model-menu .el-select-dropdown__list{padding:6px}
.playground-model-menu .el-select-dropdown__item{height:auto;min-height:74px;margin:2px 0;border-radius:7px;padding:10px 12px;line-height:normal;white-space:normal}
.playground-model-menu .el-select-dropdown__item.hover,.playground-model-menu .el-select-dropdown__item:hover{background:#f3f7ff}
.playground-model-menu .el-select-dropdown__item.selected{background:#edf4ff}
.model-option{display:flex;flex-direction:column;gap:5px}
.model-option-head{display:flex;align-items:center;justify-content:space-between;gap:12px}
.model-option-head strong{color:#26342c;font-size:12px}
.model-option-head span{flex:0 0 auto;border-radius:10px;padding:3px 7px;background:#e7efff;color:#2563eb;font-size:9px;font-weight:700}
.model-option small{color:#65736a;font-size:10px;line-height:1.45}
.model-option p{margin:0;color:#87918b;font-size:9px;line-height:1.4}
.model-option p b{color:#68756d}
.playground-model-menu .el-select-dropdown__item.selected .model-option-head strong{color:#1f58bd}
@media(max-width:767px){
  .playground-model-menu.el-select-dropdown{max-width:calc(100vw - 16px)}
  .playground-model-menu .el-select-dropdown__item{min-height:88px;padding:12px}
  .system-prompt-dialog{width:calc(100% - 16px)!important;margin-top:calc(4vh + env(safe-area-inset-top))!important}
  .system-prompt-dialog .el-dialog__body{padding:8px 12px calc(16px + env(safe-area-inset-bottom))}
  .system-prompt-dialog pre{max-height:70dvh;padding:12px;font-size:14px}
}
.message-content{margin:0;border:1px solid #e2e7e3;border-radius:9px;padding:11px 13px;background:#fff;color:#344139;font-size:12px;line-height:1.7;white-space:pre-wrap}
.message.user .message-content{border-color:#dce6f7;background:#edf4ff}
.message-content.markdown{white-space:normal}
.message-content.markdown>*:first-child{margin-top:0}
.message-content.markdown>*:last-child{margin-bottom:0}
.message-content.markdown p{margin:0 0 8px}
.message-content.markdown h1,.message-content.markdown h2,.message-content.markdown h3{margin:14px 0 7px;color:#25332b;line-height:1.4}
.message-content.markdown h1{font-size:17px}.message-content.markdown h2{font-size:15px}.message-content.markdown h3{font-size:13px}
.message-content.markdown ul,.message-content.markdown ol{margin:6px 0 9px;padding-left:20px}
.message-content.markdown li+li{margin-top:4px}
.message-content.markdown strong{color:#25332b;font-weight:700}
.message-content.markdown blockquote{margin:8px 0;border-left:3px solid #9bb5e5;padding:4px 10px;background:#f6f8fc;color:#5d6962}
.message-content.markdown code{border-radius:3px;padding:2px 4px;background:#f0f3f1;color:#b33434;font-family:Consolas,monospace;font-size:.92em}
.message-content.markdown pre{overflow:auto;margin:8px 0;border-radius:6px;padding:10px;background:#202a24;color:#eef4f0;white-space:pre}
.message-content.markdown pre code{padding:0;background:transparent;color:inherit}
.message-content.markdown a{color:#1f5dc3;text-decoration:underline;text-underline-offset:2px}
.message-content.streaming::after{width:2px;height:1em;display:inline-block;margin-left:3px;background:#2563eb;content:"";vertical-align:-2px;animation:stream-caret .8s steps(1) infinite}
.system-prompt-dialog .el-dialog__body{padding-top:8px}.system-prompt-dialog pre{max-height:min(60vh,520px);overflow:auto;margin:0;border:1px solid #e2e7e3;border-radius:7px;padding:16px;background:#f7f9f7;color:#3f4d44;font-family:inherit;font-size:12px;line-height:1.8;white-space:pre-wrap}
.welcome-state h2{font-size:21px!important;letter-spacing:.06em}
.welcome-state p{font-size:9px!important;letter-spacing:.08em}
@media(max-width:767px){
  .welcome-state h2{font-size:18px!important}
  .welcome-state p{font-size:11px!important}
}
@media(max-height:560px) and (max-width:900px){
  .welcome-state h2{font-size:15px!important}
}
.compact-mode-switch{width:70px}
.compact-mode-switch button{width:32px;padding:0}
.compact-mode-switch button span{display:none}
@media(max-width:767px){
  .compact-mode-switch{width:92px}
  .compact-mode-switch button{width:42px}
}
.quality-control .el-select{width:138px;flex:0 0 138px}
.quality-control .el-input__inner{height:28px;border:0;border-radius:7px;padding:0 27px 0 12px;background:#fff;color:#244a89;font-size:11px;font-weight:700;line-height:28px;box-shadow:inset 0 0 0 1px #d7e2f5;cursor:pointer}
.quality-control .el-input__suffix{right:5px}
.quality-control .el-input__icon{color:#6484bd;font-weight:700;line-height:28px}
.quality-control .el-select .el-input.is-focus .el-input__inner{box-shadow:inset 0 0 0 1px #7da3e7}
.playground-quality-menu.el-select-dropdown{overflow:hidden;border:1px solid #d9e4f7;border-radius:10px;box-shadow:0 12px 30px rgba(32,58,102,.16)}
.playground-quality-menu .el-select-dropdown__list{padding:5px}
.playground-quality-menu .el-select-dropdown__item{height:38px;display:flex;align-items:center;justify-content:space-between;gap:18px;margin:2px 0;border-radius:7px;padding:0 11px;color:#45566f;font-size:12px}
.quality-option-name{font-weight:600}
.quality-option-points{color:#7888a2;font-size:11px;font-variant-numeric:tabular-nums}
.playground-quality-menu .el-select-dropdown__item.selected .quality-option-points{color:#4d70ad}
.playground-quality-menu .el-select-dropdown__item:hover,.playground-quality-menu .el-select-dropdown__item.hover{background:#f1f6ff}
.playground-quality-menu .el-select-dropdown__item.selected{background:#eaf2ff;color:#245fc5;font-weight:700}
@media(max-width:767px){
  .quality-control .el-select{width:150px;flex-basis:150px}
  .quality-control .el-input__inner{height:34px;font-size:13px;line-height:34px}
  .quality-control .el-input__icon{line-height:34px}
  .playground-quality-menu .el-select-dropdown__item{height:44px;font-size:14px}
  .playground-workspace .composer-quality-selector{justify-content:flex-start}
  .playground-workspace .composer-quality-selector .quality-selector__options{
    width:100%;
    max-width:none;
    flex:1 1 auto;
    grid-template-columns:repeat(2,minmax(0,1fr))!important;
    gap:2px;
    box-sizing:border-box;
    border:1px solid #dfe5e1;
    border-radius:12px;
    padding:2px;
    background:#f3f6f4;
  }
  .playground-workspace .composer-quality-selector button{
    min-width:0;
    align-items:center;
    justify-content:center;
    flex-direction:row;
    gap:3px;
    border:0;
    border-radius:9px;
    padding:3px 4px;
    background:transparent;
    box-shadow:none;
    white-space:nowrap;
  }
  .playground-workspace .composer-quality-selector button.active{
    border:0;
    background:#fff;
    box-shadow:0 2px 6px rgba(37,99,235,.12);
  }
  .playground-workspace .composer-quality-selector strong{font-size:11px!important;line-height:1.15;white-space:nowrap}
  .playground-workspace .composer-quality-selector small{margin-top:0;font-size:11px!important;line-height:1.15}
  .playground-workspace .prompt-optimize-button:disabled{
    border-color:#d8e1ed;
    background:#f3f6fa;
    color:#8998ad;
    opacity:.78;
  }
}
</style>
